<template>
  <div ref="rootEl" class="act">
    <!-- ===================== Header ===================== -->
    <header class="act-head">
      <div class="act-head-text">
        <h2 class="act-title">{{ t('demoActions.header.title') }}</h2>
        <p class="act-sub">{{ t('demoActions.header.sub') }}</p>
      </div>
      <!-- The one number that proves the loop works — compact, always in view. -->
      <div v-if="earned.has" class="act-earned">
        <span class="act-earned-label"><i class="bi bi-stars"></i> {{ t('demoActions.header.delivered') }}</span>
        <span class="act-earned-value">{{ eurSignedFmt(earned.measured) }}</span>
        <span class="act-earned-note">
          {{ t('demoActions.header.measured') }}<template v-if="earned.since"> · {{ t('demoActions.header.since', { date: fmtLong(earned.since) }) }}</template>
        </span>
      </div>
    </header>

    <!-- Demo: open it to see what each place can measure; saving stays locked. -->
    <LogChangeForm
      v-if="showAddForm"
      :zones="demoZones"
      :today-iso="TODAY_ISO"
      locked
      @cancel="showAddForm = false"
    />

    <!-- ===================== Lifecycle tabs =====================
         One stage at a time. The lifecycle IS the navigation: a suggestion flows
         Open → In progress → Concluded, and you only ever work in one of them. -->
    <!-- Tab bar: the lifecycle tabs, and at its right end the page's one action —
         visible from every tab, never competing with the header's € figure. -->
    <div class="stage-bar">
      <nav class="stage-tabs" role="tablist" :aria-label="t('demoActions.stages.aria')">
        <template v-for="(st, i) in stages" :key="st.key">
          <span v-if="i" class="stage-arrow" aria-hidden="true"><i class="bi bi-chevron-right"></i></span>
          <button
            type="button"
            role="tab"
            class="stage-tab"
            :class="{ active: stage === st.key }"
            :aria-selected="stage === st.key"
            @click="stage = st.key"
          >
            <span class="stage-count">{{ st.count }}</span>
            <span class="stage-text">
              <span class="stage-label">{{ st.label }}</span>
              <span class="stage-hint">{{ st.hint }}</span>
            </span>
          </button>
        </template>
      </nav>
      <button
        type="button"
        class="btn-primary-sm stage-bar-btn"
        :aria-expanded="showAddForm"
        @click="showAddForm = !showAddForm"
      >
        <i class="bi bi-plus-lg"></i> {{ t('demoActions.stages.logOwn') }}
      </button>
    </div>

    <!-- ======================== OPEN ======================== -->
    <section v-show="stage === 'open'" class="stage" role="tabpanel" :aria-label="t('demoActions.open.aria')">
      <!-- Zone identity 6.7: the € on these suggestions follow a money-settings change tonight. -->
      <ChangeNotice v-if="moneyChanged" :compact="true" :text="NOTICES.settings.money" />
      <!-- Forecast heads-up: a one-off tip, not a tracked experiment. -->
      <div v-if="showWeatherHeadsUp" class="notice">
        <i class="bi bi-cloud-sun notice-ic" aria-hidden="true"></i>
        <div class="notice-body">
          <strong>{{ weatherHeadsUp.title }}.</strong> {{ weatherHeadsUp.text }}
        </div>
        <button type="button" class="link-btn" @click="weatherHeadsUpDismissed = true">{{ t('demoActions.open.noted') }}</button>
      </div>

      <div class="toolbar">
        <div class="filters" role="group" :aria-label="t('demoActions.open.filtersAria')">
          <button
            v-for="f in openFilters"
            :key="f.key"
            type="button"
            class="filter"
            :class="['kind-' + f.key, { active: openFilter === f.key }]"
            :disabled="f.key !== 'all' && !f.count"
            :aria-pressed="openFilter === f.key"
            @click="openFilter = f.key"
          >
            <span v-if="f.key !== 'all'" class="dot" aria-hidden="true"></span>
            {{ f.label }} <span class="filter-n">{{ f.count }}</span>
          </button>
        </div>
      </div>

      <p v-if="!openItems.length" class="empty-state">
        {{ t('demoActions.open.empty') }}
      </p>

      <ul class="sugg-list">
        <li
          v-for="it in visibleOpen"
          :key="it.s.id"
          class="sugg"
          :class="['kind-' + it.kind, { flash: flashZone === it.s.zone }]"
          :data-zone="it.s.zone"
        >
          <div class="sugg-main">
            <div class="meta">
              <span class="dot" aria-hidden="true"></span>
              <span class="meta-kind">{{ KIND_LABEL[it.kind] }}</span>
              <span class="meta-sep" aria-hidden="true">·</span>
              <span class="meta-zone">{{ it.s.zone }}</span>
              <span v-if="it.kind === 'money'" class="meta-lever">{{ it.s.lever }}</span>
              <span v-if="it.s.pinned" class="meta-tag">{{ t('demoActions.open.inConsideration') }}</span>
              <Tooltip v-if="execFlagFor(it.s)" :text="execFlagFor(it.s).note" placement="top" align="start">
                <span class="meta-tag tag-warn"><i class="bi bi-arrow-repeat"></i> {{ t('demoActions.open.triedBefore') }}</span>
              </Tooltip>
              <Tooltip
                v-if="it.s.basis"
                :text="t('demoActions.open.belowBestTip', { now: it.s.basis.now, best: it.s.basis.best, wobble: it.s.basis.wobble })"
                placement="top"
                align="start"
              >
                <span class="meta-tag"><i class="bi bi-info-circle"></i> {{ t('demoActions.open.belowBest') }}</span>
              </Tooltip>
            </div>

            <p class="sugg-action">{{ it.s.action }}</p>

            <button
              type="button"
              class="why-toggle"
              :aria-expanded="!!expanded[it.s.id]"
              @click="toggleExpanded(it.s.id)"
            >
              {{ t('demoActions.open.whyToggle') }}
              <i class="bi" :class="expanded[it.s.id] ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </button>
            <div v-if="expanded[it.s.id]" class="sugg-why">
              <p>{{ it.s.why }}</p>
              <p v-if="execFlagFor(it.s)" class="sugg-why-note">
                {{ t('demoActions.open.execNote') }}
              </p>
            </div>
          </div>

          <div class="sugg-side">
            <div class="value" :class="{ 'value-muted': !suggValue(it).main }">
              <template v-if="suggValue(it).main">{{ suggValue(it).main }}</template>
              <i v-else class="bi bi-people" aria-hidden="true"></i>
            </div>
            <div class="value-unit">{{ suggValue(it).unit }}</div>

          </div>

          <div v-if="!it.s.confirming" class="sugg-buttons">
            <button type="button" class="btn-primary-sm" @click="startImplement(it.s)">
              <i class="bi bi-check2-circle"></i> {{ t('demoActions.open.implemented') }}
            </button>
            <button type="button" class="btn-ghost-sm" :class="{ on: it.s.pinned }" @click="markConsidering(it.s)">
              <i class="bi bi-hourglass-split"></i> {{ t('demoActions.open.inConsideration') }}
            </button>
            <!-- Demo: shown, not usable — they would remove the card from the story. -->
            <Tooltip :text="t('demoActions.open.liveOnly')" placement="top" align="center">
              <button type="button" class="btn-ghost-sm is-locked" aria-disabled="true">
                <i class="bi bi-lock-fill lock-ic"></i> {{ t('demoActions.open.maybeLater') }}
              </button>
            </Tooltip>
            <Tooltip :text="t('demoActions.open.liveOnly')" placement="top" align="center">
              <button type="button" class="btn-ghost-sm danger is-locked" aria-disabled="true">
                <i class="bi bi-lock-fill lock-ic"></i> {{ t('demoActions.open.neverAgain') }}
              </button>
            </Tooltip>
          </div>

          <!-- Inline confirm: date of the change, or snooze length -->
          <div v-if="it.s.confirming === 'implement'" class="inline-form">
            <label>{{ t('demoActions.open.whenChange') }}
              <input type="date" v-model="it.s.pivotDraft" :max="TODAY_ISO" />
            </label>
            <button type="button" class="btn-primary-sm" @click="implement(it.s)">{{ t('demoActions.open.trackIt') }}</button>
            <button type="button" class="link-btn" @click="it.s.confirming = null">{{ t('demoActions.open.cancel') }}</button>
          </div>
          <div v-else-if="it.s.confirming === 'snooze'" class="inline-form">
            <label>{{ t('demoActions.open.remindIn') }}
              <select v-model.number="it.s.snoozeDraft">
                <option :value="14">{{ t('demoActions.open.weeks2') }}</option>
                <option :value="30">{{ t('demoActions.open.month1') }}</option>
                <option :value="90">{{ t('demoActions.open.months3') }}</option>
              </select>
            </label>
            <button type="button" class="btn-primary-sm" @click="snooze(it.s)">{{ t('demoActions.open.snooze') }}</button>
            <button type="button" class="link-btn" @click="it.s.confirming = null">{{ t('demoActions.open.cancel') }}</button>
          </div>
        </li>
      </ul>

      <!-- Held back by what you concluded — quiet, collapsed, at the bottom. -->
      <details v-if="heldBackSuggestions.length && openFilter !== 'waiting' && openFilter !== 'reach'" class="held">
        <summary class="held-toggle">
          <i class="bi bi-slash-circle"></i>
          {{ t(heldBackSuggestions.length === 1 ? 'demoActions.open.heldOne' : 'demoActions.open.heldMany', { n: heldBackSuggestions.length }) }}
          <span class="held-cta">
            <span class="held-show">{{ t('demoActions.open.show') }}</span><span class="held-hide">{{ t('demoActions.open.hide') }}</span>
            <i class="bi bi-chevron-down"></i>
          </span>
        </summary>
        <div v-for="s in heldBackSuggestions" :key="s.id" class="held-row">
          <div>
            <span class="meta-zone">{{ s.zone }}</span> — {{ s.action }}
            <div class="held-reason">{{ t('demoActions.open.heldReason') }}</div>
          </div>
          <Tooltip :text="t('demoActions.open.liveOnly')" placement="top" align="end">
            <button type="button" class="link-btn is-locked" aria-disabled="true">
              <i class="bi bi-lock-fill lock-ic"></i> {{ t('demoActions.open.reconsider') }}
            </button>
          </Tooltip>
        </div>
      </details>
    </section>

    <!-- ===================== IN PROGRESS ===================== -->
    <section v-show="stage === 'progress'" class="stage" role="tabpanel" :aria-label="t('demoActions.progress.aria')">
      <!-- Tracked value: measured is the headline; modelled kept apart -->
      <div v-if="rollup.hasNumbers" class="summary">
        <div class="summary-main">
          <span class="summary-label">{{ t('demoActions.progress.tracked') }}</span>
          <span class="summary-value">{{ eurSignedFmt(rollup.measured) }}<span class="summary-per">{{ ' ' + t('demoActions.progress.perMonth') }}</span></span>
          <Tooltip
            :text="t('demoActions.progress.trackedTip')"
            placement="bottom"
            align="start"
          >
            <span class="summary-tag">{{ t('demoActions.progress.measured') }} <i class="bi bi-info-circle"></i></span>
          </Tooltip>
        </div>
        <div class="summary-extra">
          <span v-if="rollup.modelled !== 0">{{ t('demoActions.progress.modelled', { eur: eurSignedFmt(rollup.modelled) }) }}</span>
          <span v-if="rollup.building">{{ t('demoActions.progress.building', { n: rollup.building }) }}</span>
          <span v-if="rollup.unquantified">{{ t('demoActions.progress.unquantified', { n: rollup.unquantified }) }}</span>
        </div>
      </div>

      <div class="toolbar">
        <div class="filters" role="group" :aria-label="t('demoActions.progress.filtersAria')">
          <button
            v-for="f in progressFilters"
            :key="f.key"
            type="button"
            class="filter"
            :class="['st-' + f.key, { active: progressFilter === f.key }]"
            :disabled="f.key !== 'all' && !f.count"
            :aria-pressed="progressFilter === f.key"
            @click="progressFilter = f.key"
          >
            {{ f.label }} <span class="filter-n">{{ f.count }}</span>
          </button>
        </div>
      </div>

      <p v-if="!tracked.length" class="empty-state">
        {{ t('demoActions.progress.empty') }}
      </p>

      <ul class="track-list">
        <li
          v-for="a in visibleTracked"
          :key="a.id"
          class="track"
          :class="['kind-' + kindOf(a), { open: expanded[a.id] }]"
        >
          <button
            type="button"
            class="track-row"
            :aria-expanded="!!expanded[a.id]"
            @click="toggleExpanded(a.id)"
          >
            <span class="track-id">
              <span class="meta">
                <span class="dot" aria-hidden="true"></span>
                <span class="meta-kind">{{ KIND_LABEL[kindOf(a)] }}</span>
                <span class="meta-sep" aria-hidden="true">·</span>
                <span class="meta-zone">{{ a.scope || t('demoActions.progress.wholeStore') }}</span>
                <span v-if="noSalesAdded(a)" class="meta-tag tag-nomoney">
                  <i class="bi bi-receipt"></i> {{ t('demoActions.progress.noSales') }}
                </span>
              </span>
              <span class="track-what">{{ a.what }}</span>
              <span v-if="typeChangeLine(a)" class="track-type-note">
                <i class="bi bi-arrow-repeat"></i> {{ typeChangeLine(a) }}
              </span>
            </span>

            <span v-if="headlineReading(a)" class="track-reading">
              <span class="track-reading-label">{{ headlineReading(a).label }}</span>
              <span class="track-reading-value" :class="headlineReading(a).cls">{{ headlineReading(a).text }}</span>
            </span>

            <span class="track-status">
              <template v-if="isVerdict(a)">
                <span class="verdict-chip" :class="verdictPill(a).cls">{{ verdictPill(a).label }}</span>
                <span class="track-status-note">{{ t('demoActions.progress.readyToConclude') }}</span>
              </template>
              <template v-else>
                <span class="progress" :aria-label="t('demoActions.progress.dayOfAria', { n: elapsedDays(a) })">
                  <span class="progress-fill" :style="{ width: measureProgress(a) + '%' }"></span>
                </span>
                <span class="track-status-note">{{ t('demoActions.progress.dayOf', { n: elapsedDays(a) }) }}</span>
              </template>
            </span>

            <i class="bi track-chev" :class="expanded[a.id] ? 'bi-chevron-up' : 'bi-chevron-down'" aria-hidden="true"></i>
          </button>

          <div v-if="expanded[a.id]" class="track-detail">
            <p class="read" v-html="readSentence(a)"></p>
            <p v-if="a.weatherCaveat" class="read read-weather">
              <i class="bi bi-cloud-sun"></i> {{ a.weatherCaveat }}
            </p>

            <div class="impact" :class="impactView(a).cls">
              <i class="bi bi-cash-coin"></i>
              <template v-if="impactView(a).kind === 'na'">
                <span class="imp-text">{{ t('demoActions.impact.na') }}</span>
              </template>
              <template v-else-if="impactView(a).kind === 'none'">
                <span class="imp-text">{{ t('demoActions.impact.none') }}</span>
              </template>
              <template v-else>
                <span class="imp-text">
                  <template v-if="impactView(a).early">{{ t('demoActions.impact.onTrack') + ' ' }}</template>
                  <template v-else>{{ t('demoActions.impact.label') + ' ' }}</template>
                  <b>{{ impactView(a).value }}</b> {{ t('demoActions.impact.perMo') }}
                </span>
                <Tooltip :text="impactView(a).tip" placement="top" align="start">
                  <span class="imp-basis">{{ impactView(a).sub }} <i class="bi bi-info-circle"></i></span>
                </Tooltip>
              </template>
            </div>

            <div class="delta-row">
              <template v-for="r in a.readings" :key="r.metric">
                <Tooltip
                  v-if="r.pending"
                  :text="t('demoActions.chips.pendingTip')"
                  placement="top"
                  align="start"
                >
                  <span class="delta-chip chip-pending">
                    {{ metricLabel(r.metric, a.scope) }}
                    <span class="pending-tag"><i class="bi bi-lock-fill"></i> {{ t('demoActions.chips.needsSales') }}</span>
                  </span>
                </Tooltip>
                <Tooltip v-else :text="t('demoActions.chips.showOnChart')" placement="top" align="start">
                  <button
                    type="button"
                    class="delta-chip"
                    :class="[deltaClass(r.metric, r.deltaPct),
                             { 'chip-active': a.focus === r.metric, 'chip-clickable': true }]"
                    @click="a.focus = r.metric"
                  >
                    {{ metricLabel(r.metric, a.scope) }}
                    <strong>{{ levelText(a, r) }}</strong>
                  </button>
                </Tooltip>
              </template>
            </div>

            <div v-if="a.showSales" class="sales-inline">
              <div class="add-head">
                <strong>{{ t('demoActions.sales.title', { category: a.category }) }}</strong>
                <span class="add-note">
                  {{ t('demoActions.sales.note', { def: salesDef(a) }) }}
                </span>
              </div>
              <div class="fld-row">
                <label class="fld">
                  <span>{{ t('demoActions.sales.date') }}</span>
                  <input type="date" v-model="a.saleDraft.date" :max="TODAY_ISO" />
                </label>
                <label class="fld">
                  <span>{{ t('demoActions.sales.transactions') }}</span>
                  <input type="number" min="0" v-model="a.saleDraft.transactions" :placeholder="t('demoActions.sales.eg37')" />
                </label>
                <label class="fld">
                  <span>{{ t('demoActions.sales.revenueOptional') }}</span>
                  <input type="number" min="0" v-model="a.saleDraft.revenue" :placeholder="t('demoActions.sales.eg1840')" />
                </label>
              </div>
              <div class="mini-actions">
                <button
                  class="btn-primary-sm"
                  :disabled="!(Number(a.saleDraft.transactions) > 0)"
                  @click="saveActionSale(a)"
                >
                  {{ t('demoActions.sales.save') }}
                </button>
                <button class="link-btn" @click="a.showSales = false">{{ t('demoActions.sales.close') }}</button>
              </div>
              <table v-if="a.salesLog && a.salesLog.length" class="sales-log">
                <thead>
                  <tr><th>{{ t('demoActions.sales.date') }}</th><th>{{ t('demoActions.sales.transactions') }}</th><th>{{ t('demoActions.sales.revenue') }}</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(r, i) in a.salesLog" :key="i">
                    <td>{{ r.date }}</td>
                    <td>{{ r.transactions }}</td>
                    <td>{{ r.revenue != null ? eurPlain(r.revenue) : '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <v-chart class="chart" :option="chartOption(a)" autoresize />
            <p class="chart-caption">
              {{ t('demoActions.detail.chartCaption', { metric: metricLabel(a.focus, a.scope), n: elapsedDays(a), date: fmtLong(a.pivot) }) }}
            </p>

            <div v-if="!a.concluding" class="detail-actions">
              <!-- Demo: shown, not usable — data entry and a permanent move. -->
              <Tooltip v-if="noSalesAdded(a)" :text="t('demoActions.open.liveOnly')" placement="top" align="start">
                <button type="button" class="btn-ghost-sm is-locked" aria-disabled="true">
                  <i class="bi bi-lock-fill lock-ic"></i> {{ t('demoActions.detail.addSales') }}
                </button>
              </Tooltip>
              <Tooltip :text="t('demoActions.open.liveOnly')" placement="top" align="start">
                <button type="button" class="btn-ghost-sm is-locked" aria-disabled="true">
                  <i class="bi bi-lock-fill lock-ic"></i> {{ t('demoActions.detail.conclude') }}
                </button>
              </Tooltip>
            </div>

            <div v-else class="conclude-panel">
              <div class="add-head">
                <strong>{{ t('demoActions.conclude.title', { scope: a.scope || t('demoActions.progress.wholeStore') }) }}</strong>
                <span class="add-note">
                  {{ t('demoActions.conclude.measuredResult') }} <b>{{ verdictPill(a).label }}</b>{{ t('demoActions.conclude.explain') }}
                </span>
              </div>
              <div class="attr-list">
                <label
                  v-for="opt in attributions"
                  :key="opt.key"
                  class="attr-opt"
                  :class="{ 'attr-sel': a.concludeAttr === opt.key }"
                >
                  <input type="radio" :value="opt.key" v-model="a.concludeAttr" />
                  <span>{{ opt.label }}</span>
                </label>
              </div>
              <label class="fld">
                <span>{{ t('demoActions.conclude.note') }}</span>
                <textarea
                  v-model="a.concludeNote"
                  rows="2"
                  :placeholder="t('demoActions.conclude.notePlaceholder')"
                ></textarea>
              </label>
              <p class="allen-line"><i class="bi bi-robot"></i> {{ attrMeta(a.concludeAttr).allen }}</p>
              <div class="mini-actions">
                <button class="btn-primary-sm" @click="confirmConclude(a)">{{ t('demoActions.conclude.record') }}</button>
                <button class="link-btn" @click="cancelConclude(a)">{{ t('demoActions.conclude.cancel') }}</button>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </section>

    <!-- ====================== CONCLUDED ====================== -->
    <section v-show="stage === 'done'" class="stage" role="tabpanel" :aria-label="t('demoActions.done.aria')">
      <p class="stage-intro">
        {{ t('demoActions.done.intro') }}
      </p>
      <p v-if="!concluded.length" class="empty-state">{{ t('demoActions.done.empty') }}</p>
      <ul class="learned-list">
        <li v-for="c in concluded" :key="c.id" class="learned" :class="'kind-' + kindOf(c)">
          <div class="learned-top">
            <span class="meta">
              <span class="dot" aria-hidden="true"></span>
              <span class="meta-kind">{{ KIND_LABEL[kindOf(c)] }}</span>
              <span class="meta-sep" aria-hidden="true">·</span>
              <span class="meta-zone">{{ c.scope || t('demoActions.progress.wholeStore') }}</span>
              <span class="meta-date">{{ fmtLong(c.pivot) }}</span>
            </span>
            <span class="learned-chips">
              <span class="verdict-chip" :class="verdictClsFor(c.verdict)">{{ verdictLabelFor(c.verdict) }}</span>
              <span class="attr-chip" :class="attrMeta(c.attribution).chip">{{ attrMeta(c.attribution).short }}</span>
            </span>
          </div>
          <p class="learned-what">{{ c.what }}</p>
          <!-- §9 item 5: a concluded action keeps the result it had that day. -->
          <p v-if="c.measuredUntil" class="learned-measured">
            <i class="bi bi-lock"></i> {{ t('demoActions.done.measuredUntil', { day: sayDay(c.measuredUntil) }) }}
          </p>
          <p v-if="c.note" class="learned-note">“{{ c.note }}”</p>
          <p class="allen-line"><i class="bi bi-robot"></i> {{ attrMeta(c.attribution).allen }}</p>
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, nextTick, defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatEuro } from '@/i18n/format'
// Dutch next to English: UI strings in i18n/locales/*/demoActions.json, the sample
// suggestions / notes as withNl data (read in the current language).
import { withNl, demoIsNl } from '@/demo/demoLang'
import { useRoute } from 'vue-router'
import { NOTICES, ZONE_TYPE_NAMES } from '@/zones/changesToday'
import { changeFor, registerFor } from '@/demo/changesTodayDemo'
import { settingChanged } from '@/zones/changesToday'
import ChangeNotice from '@/components/ui/ChangeNotice.vue'

import Section from '@/components/ui/Section.vue'
import Tooltip from '@/components/ui/Tooltip.vue'
import { loadDemoJSON } from '@/api/demoData.js'
import { sayDay } from '@/zones/zoneMarkers'
// Dates, series builders, zone economics, the tracked seed list and the € math are
// shared with the demo home (demo/guidanceDemo.js) so both show the same figures.
import {
  demoDaysAgo, demoDateStr, TODAY, DAY, pad2, iso, addDays, MONTHS,
  mulberry, makeSeries, seriesSet, ZONE_ECON, SERVICE_ECON, IMPACT_FLOOR_EUR, meanSide,
  elapsedDays, isVerdict, finishImpact, computeImpact, buildDemoTracked,
} from '@/demo/guidanceDemo.js'
import LogChangeForm from '@/components/guidance/LogChangeForm.vue'

import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, MarkLineComponent, MarkAreaComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

use([LineChart, GridComponent, TooltipComponent, MarkLineComponent, MarkAreaComponent, CanvasRenderer])



// The four levers the € model runs on. traffic + dwell come straight from the
// daily series; engagement is the frozen-quick-pass stop-rate (needs the
// pipeline to store a per-day engaged count against a baseline threshold);
// conversion = buyers ÷ engaged, fed manually per category until POS.
// Each metric carries a good-direction: +1 = higher is better, -1 = lower is
// better (a shorter queue is a win), 0 = informational (no good/bad).
const METRIC = {
  // Labelled by scope in metricLabel(): store traffic store-wide, zone traffic on a zone.
  traffic: { label: 'Traffic', fmt: (v) => `${v}`, axis: '{value}', goodDir: +1 },
  // Reach = zone traffic ÷ store traffic — the verdict metric for a reach change.
  // Needs counts only, so a tripwire zone has it too.
  reach: { label: 'Reach', fmt: (v) => `${v}%`, axis: '{value}%', goodDir: +1 },
  // Customers nobody helped, per open day — fewer is the win.
  service_response: { label: 'Not helped', fmt: (v) => `${v} per day`, axis: '{value}', goodDir: -1 },
  dwell: { label: 'Dwell time', fmt: (v) => `${v}s`, axis: '{value}s', goodDir: +1 },
  // Engagement = clear-lingerers ÷ zone visitors (the ones who stopped).
  engagement: { label: 'Engagement', fmt: (v) => `${v}%`, axis: '{value}%', goodDir: +1 },
  // Conversion = buyers ÷ ENGAGED (EngagersConversionRate) — matches the
  // Intelligence funnel, product-zones only. NOT buyers ÷ footfall.
  conversion: { label: 'Conversion', fmt: (v) => `${v}%`, axis: '{value}%', goodDir: +1 },
  // Purchase rate = buyers ÷ store visitors — the whole funnel (engagement ×
  // conversion). A store KPI, deliberately named apart from "conversion".
  purchase_rate: { label: 'Purchase rate', fmt: (v) => `${v}%`, axis: '{value}%', goodDir: +1 },
  // Checkout: the daily series' average_duration on a waiting/interaction zone
  // *is* the real wait / service time — labelled and oriented correctly here.
  wait: { label: 'Wait time', fmt: (v) => `${v}s`, axis: '{value}s', goodDir: -1 },
  interaction: { label: 'Interaction time', fmt: (v) => `${v}s`, axis: '{value}s', goodDir: -1 },
  wait_rate: { label: 'Wait rate', fmt: (v) => `${v}%`, axis: '{value}%', goodDir: 0 },
  txn_rate: { label: 'Transaction rate', fmt: (v) => `${v}%`, axis: '{value}%', goodDir: +1 },
}

// What each scope can actually measure, primary lever first. Store: traffic +
// conversion. Entrance: traffic only. Checkout waiting: the real WAIT time +
// wait rate. Checkout interaction: service time + transaction rate. Product
// zones: engagement/conversion + dwell/traffic. Mirrors the real constraints.
const CAPABILITIES = {
  'Whole store': ['traffic', 'purchase_rate'],
  'Main Entrance': ['traffic'],
  'Power Tools': ['engagement', 'conversion', 'dwell', 'reach', 'traffic'],
  'Safety Shoes': ['engagement', 'conversion', 'dwell', 'reach', 'traffic'],
  'Garden Utilities': ['engagement', 'conversion', 'dwell', 'reach', 'traffic', 'service_response'],
  'Promoted Articles Zone': ['engagement', 'conversion', 'dwell', 'reach', 'traffic'],
  // Tripwire-only: counts who arrives, so reach + zone traffic — no engagement,
  // conversion or per-visitor dwell. Mirrors what production measures.
  'Paint & Decor': ['reach', 'traffic'],
  'Checkout 1 Waiting': ['wait', 'wait_rate', 'traffic'],
  // The store-level checkout staffing suggestion — judged on the queue wait.
  'Checkout': ['wait', 'wait_rate', 'traffic'],
  'Checkout 1 Interaction': ['interaction', 'txn_rate', 'traffic'],
  'Customer Service Desk': ['dwell', 'traffic'],
}
const capsFor = (scope) => CAPABILITIES[scope || 'Whole store'] || ['traffic']

// A zone inherits its sales category; conversion = that category's transactions
// ÷ the zone's footfall. Only scopes that map here can carry conversion.
const ZONE_CATEGORY = {
  'Whole store': 'Store-wide',
  'Power Tools': 'Power Tools',
  'Safety Shoes': 'Safety Shoes',
  'Garden Utilities': 'Garden',
  'Promoted Articles Zone': 'Promoted Articles',
}


const BASE_MEAN = {
  traffic: 30, reach: 15, service_response: 0.8, dwell: 20, engagement: 40, conversion: 18, purchase_rate: 9,
  wait: 70, interaction: 45, wait_rate: 38, txn_rate: 82,
}

// Metrics that can't be read until the store feeds sales numbers.
const SALES_METRICS = ['conversion', 'purchase_rate']

const roundEur = (n) => Math.round(Math.abs(n) / 10) * 10
const eurAbs = (n) => (demoIsNl()
  ? formatEuro(roundEur(n), { lang: 'nl' })
  : `€${roundEur(n).toLocaleString('en-US')}`)
// A typed sales amount as entered ("€1840" / "€ 1.840").
const eurPlain = (n) => (demoIsNl() ? formatEuro(n, { lang: 'nl' }) : '€' + n)
// A metric value in Dutch: decimal comma, "sec", "per dag".
const nlNum = (v) => String(v).replace('.', ',')
const fmtMetric = (m, v) => {
  const meta = METRIC[m]
  if (!meta) return `${v}`
  if (!demoIsNl()) return meta.fmt(v)
  if (meta.axis === '{value}%') return `${nlNum(v)}%`
  if (meta.axis === '{value}s') return `${nlNum(v)} sec`
  if (m === 'service_response') return `${nlNum(v)} per dag`
  return nlNum(v)
}
// Short Dutch months for dates and chart labels ("3 okt").
const NL_MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']
const eurSigned = (n) => `${n < 0 ? '−' : '+'}${eurAbs(n)}`

// Concluding an action records WHY it landed where it did — the measurement can't
// tell a bad idea from bad execution, so the human attributes it. That attribution
// is the signal Allen learns from (suppress vs. retry vs. repeat).
const ATTRIBUTIONS = [
  { key: 'worked', label: 'It worked — keep it', short: 'Worked', chip: 'attr-good',
    allen: 'Allen will look to repeat this where it fits, and won’t re-pitch it here.' },
  { key: 'exec', label: 'Idea’s sound — execution fell short', short: 'Execution fell short', chip: 'attr-warn',
    allen: 'Allen keeps the idea alive — it may resurface with guidance to execute it properly.' },
  { key: 'drop', label: 'Didn’t fit here — don’t suggest again', short: 'Won’t suggest again', chip: 'attr-bad',
    allen: 'Allen won’t suggest this for this zone again.' },
  { key: 'inconclusive', label: 'Inconclusive — set aside', short: 'Inconclusive', chip: 'attr-flat',
    allen: 'Set aside — not counted for or against the idea.' },
]
const ATTR_BY_KEY = Object.fromEntries(ATTRIBUTIONS.map((a) => [a.key, a]))
// The shown texts come from demoActions.attr.<key> (same English as above).
// A concluded action stores the verdict KEY; the label is looked up when shown,
// so it can be said in the viewer's language.
const VERDICT_CLS = {
  improved: 'v-improved', declined: 'v-declined', no_change: 'v-flat',
  mixed: 'v-mixed', building_up: 'v-early',
}
const VERDICT_LABEL = {
  improved: 'Improved', declined: 'Declined', no_change: 'No change',
  mixed: 'Mixed', building_up: 'Building up',
}

// A just-logged action: mostly "before" history with a short, flat "after" —
// the honest day-one picture, before the change has had time to land. `chosen`
// is the list of metric keys to watch (already filtered to what's measurable).
function freshTracked(id, scope, what, pivot, chosen, idea = null, kind = null) {
  const el = Math.max(1, Math.round((TODAY - new Date(pivot + 'T00:00:00')) / DAY))
  const metrics = chosen && chosen.length ? chosen : ['traffic']
  const means = {}
  metrics.forEach((m) => {
    means[m] = [BASE_MEAN[m] ?? 20, BASE_MEAN[m] ?? 20] // flat: change hasn't landed
  })
  // Conversion / purchase rate can't be read until the store feeds sales for the
  // category — until then they're pending (no chip value, no sway on the verdict).
  const needsSales = metrics.some((m) => SALES_METRICS.includes(m))
  const isPending = (m) => SALES_METRICS.includes(m)
  return {
    id,
    idea, // signature that ties a concluded outcome back to a suggestion
    // The metric this action is MEANT to move — the suggestion's lever, or the first
    // metric you picked for your own idea. The row's headline always shows THIS one,
    // never a fallback to whatever happens to have a value.
    goal: metrics[0],
    kind, // which Open group it came from (null → derived; manual → 'own')
    scope: !scope || scope === 'Whole store' ? '' : scope,
    category: ZONE_CATEGORY[scope || 'Whole store'] || scope || 'Store-wide',
    what,
    pivot,
    focus: metrics.find((m) => !isPending(m)) || metrics[0],
    series: seriesSet(pivot, el, means, 5000 + el),
    readings: metrics.map((m) =>
      isPending(m) ? { metric: m, deltaPct: null, pending: true } : { metric: m, deltaPct: 0.0 }
    ),
    salesProvided: false, // no sales added yet
    showSales: false,
    salesLog: [],
    saleDraft: { date: iso(TODAY), transactions: null, revenue: null },
  }
}

export default defineComponent({
  name: 'StoralyticDemoGuidance',

  components: { Section, Tooltip, VChart, LogChangeForm, ChangeNotice },

  props: {
    focusZone: { type: String, default: '' },   // deep-link: scroll to + flash this zone's card
  },

  setup(props) {
    const { t } = useI18n()
    // ---------------- Suggestions inbox (mock) ----------------
    // Distinct from the already-implemented Tracked Actions below (Garden
    // Utilities, Promoted Articles) — these are still-open opportunities.
    // Service-family suggestions — no € figure, deliberately. We declined to put a
    // euro on a missed customer or on a queue, so a money-ranked list has no correct
    // position for them; they get their own block above it. Mirrors production
    // GuidanceTab, which builds these from the proactive findings feed.
    const serviceSuggestions = ref([
      withNl({
        id: 'sv1',
        idea: 'svc:garden-utilities',
        zone: 'Garden Utilities',
        lever: 'Service',
        leverKey: 'svc',
        action: 'Customers going unserved at one time of week — Garden Utilities',
        why: '15 customers stayed long enough in Garden Utilities to be worth approaching '
           + 'and nobody came over. 11 of the 15 were on a Saturday, mostly between 14:00 '
           + 'and 18:00. That is a staffing pattern rather than bad luck.',
        state: 'considering', confirming: null, pivotDraft: iso(TODAY), snoozeDraft: 14,
      }, {
        action: 'Klanten die op één moment van de week niet geholpen worden — Garden Utilities',
        why: '15 klanten bleven lang genoeg in Garden Utilities om aangesproken te worden, '
           + 'en niemand kwam naar hen toe. 11 van de 15 waren op een zaterdag, vooral tussen 14:00 '
           + 'en 18:00. Dat is een patroon in de bezetting, geen pech.',
      }),
      withNl({
        id: 'sv2',
        idea: 'chk:checkout-staffing-pattern-friday',
        zone: 'Checkout',
        lever: 'Checkout',
        leverKey: 'chk',
        action: 'Longer checkout queues on Fridays',
        why: 'Between 15:00 and 18:00 your queue runs about 4.8 people with 2.0 tills '
           + 'open — roughly 2.4 per till, against 0.6 the rest of the week.',
        state: 'considering', confirming: null, pivotDraft: iso(TODAY), snoozeDraft: 14,
      }, {
        action: 'Langere wachtrijen aan de kassa op vrijdag',
        why: 'Tussen 15:00 en 18:00 staan er zo\'n 4,8 mensen in de rij bij 2,0 open kassa\'s '
           + '— ongeveer 2,4 per kassa, tegenover 0,6 de rest van de week.',
      }),
    ])

    const suggestions = ref([
      withNl({
        id: 'sg2',
        idea: 'garden-focal-display',
        zone: 'Garden Utilities',
        lever: 'Engagement',
        leverKey: 'engagement',
        eur: '€21,120 / mo',
        action:
          'Build a small focal display at the mouth of the garden aisle — one hero product staged for use — so passing shoppers have a concrete reason to stop, not just a rack to walk past.',
        why:
          'Around 42% of your shoppers pass Garden but only ~22% stop. Traffic isn\'t the problem — the range just doesn\'t catch the eye from the aisle. This is your biggest single opportunity right now.',
        state: 'considering',
        confirming: null,
        pivotDraft: iso(TODAY),
        snoozeDraft: 14,
      }, {
        lever: 'Engagement',
        eur: '€ 21.120 / mo',
        action:
          'Bouw een kleine blikvanger aan het begin van het tuingangpad — één topproduct, opgesteld alsof het in gebruik is — zodat voorbijlopende bezoekers een concrete reden hebben om te blijven staan, en niet alleen een rek om langs te lopen.',
        why:
          'Zo\'n 42% van je bezoekers komt langs Garden, maar slechts ~22% blijft staan. De drukte is het probleem niet — het assortiment valt vanuit het gangpad gewoon niet op. Dit is nu je grootste afzonderlijke kans.',
      }),
      withNl({
        id: 'sg3',
        idea: 'size-run-aisle-edge',
        zone: 'Safety Shoes',
        lever: 'Reach',
        leverKey: 'reach',
        eur: '€16,700 / mo',
        action:
          'Bring a low display of the full size run to the aisle edge with a clear "sizes 39–48 in stock" sign, so the category registers from the main walkway and more shoppers come in.',
        why:
          'Only about 7% of passing shoppers reach this aisle — versus ~15% at its best. The rack sits deep in the bay, off the main route, so people never find it; the ones who do engage and buy well.',
        state: 'considering',
        confirming: null,
        pivotDraft: iso(TODAY),
        snoozeDraft: 14,
      }, {
        lever: 'Bereik',
        eur: '€ 16.700 / mo',
        action:
          'Zet een lage display met alle maten aan de rand van het gangpad, met een duidelijk bord "maten 39–48 op voorraad", zodat de categorie opvalt vanaf het hoofdpad en meer bezoekers binnenlopen.',
        why:
          'Slechts zo\'n 7% van de bezoekers die voorbijkomen, gaat dit gangpad in — tegenover ~15% in de beste weken. Het rek staat ver achterin, buiten de hoofdroute, dus mensen vinden het niet; wie het wel vindt, blijft staan en koopt goed.',
      }),
      withNl({
        id: 'sg4',
        idea: 'carpark-board',
        zone: 'Main Entrance',
        lever: 'Engagement',
        leverKey: 'engagement',
        eur: '€980 / mo',
        action:
          'Mid-morning is your quietest stretch. A sandwich board at the car-park entrance featuring the week\'s project deal pulls more of the passing footfall inside.',
        why:
          'Plenty of cars in the lot but few become store entries before noon — the draw to come in just isn\'t there yet. We can watch store entries move after you place it.',
        state: 'considering',
        confirming: null,
        pivotDraft: iso(TODAY),
        snoozeDraft: 14,
      }, {
        lever: 'Engagement',
        eur: '€ 980 / mo',
        action:
          'Halverwege de ochtend is het in je winkel het rustigst. Een stoepbord aan de ingang van de parkeerplaats met de projectdeal van de week trekt meer mensen die voorbijkomen naar binnen.',
        why:
          'Er staan genoeg auto\'s op de parkeerplaats, maar vóór 12:00 worden daar weinig winkelbezoeken van — de reden om binnen te komen ontbreekt nog. Na het plaatsen kunnen we zien of het aantal winkelbezoeken stijgt.',
      }),
      withNl({
        // Collides with a concluded "drop" below → held back by what we learned.
        id: 'sg5',
        idea: 'garden-banner',
        zone: 'Garden Utilities',
        lever: 'Engagement',
        leverKey: 'engagement',
        eur: '€610 / mo',
        action:
          'Hang a seasonal banner over the garden aisle so the range is visible from the main walkway.',
        why:
          'The category sits off the main sightline; overhead signage is a low-effort way to pull passing shoppers in.',
        state: 'considering',
        confirming: null,
        pivotDraft: iso(TODAY),
        snoozeDraft: 14,
      }, {
        lever: 'Engagement',
        eur: '€ 610 / mo',
        action:
          'Hang een seizoensbanner boven het tuingangpad, zodat het assortiment zichtbaar is vanaf het hoofdpad.',
        why:
          'De categorie ligt buiten de hoofdzichtlijn; een bord boven het pad is een eenvoudige manier om voorbijlopende bezoekers binnen te trekken.',
      }),
      withNl({
        id: 'sg6',
        idea: 'promo-price-card',
        zone: 'Promoted Articles Zone',
        lever: 'Conversion',
        leverKey: 'conversion',
        eur: '€6,272 / mo',
        action:
          'Add a bold price-and-benefit card and a limited-time cue to the promo fixture, so the shoppers who stop get a clear reason to buy now — not just something nice to look at.',
        why:
          'Shoppers stop at the promo — engagement is fine — but only about 14% go on to buy. The offer isn\'t closing the sale, so conversion is the lever here.',
        state: 'considering',
        confirming: null,
        pivotDraft: iso(TODAY),
        snoozeDraft: 14,
      }, {
        lever: 'Conversie',
        eur: '€ 6.272 / mo',
        action:
          'Voeg een opvallend kaartje met prijs en voordeel en een signaal "tijdelijk aanbod" toe aan de promo-opstelling, zodat bezoekers die blijven staan een duidelijke reden krijgen om nu te kopen — niet alleen iets moois om naar te kijken.',
        why:
          'Bezoekers blijven staan bij de promo — de engagement is prima — maar slechts zo\'n 14% koopt daarna. Het aanbod overtuigt niet om te kopen, dus conversie is hier de hefboom.',
      }),
    ])

    const openSuggestions = computed(() =>
      suggestions.value.filter((s) => s.state !== 'dismissed' && s.state !== 'snoozed')
    )

    // Reach suggestions for TRIPWIRE zones — sized in visitors, not €. A tripwire counts
    // who arrives, not who stops or buys, so there's no funnel to put a euro on; the
    // opportunity is the gap to the zone's own best reach × store traffic. Own block,
    // because visitors and € can't be ranked against each other. Mirrors production.
    //   Paint & Decor: 1,716 zone visitors ÷ 11,320 store visitors ≈ 15.2% reach;
    //   best weeks 19% → 3.84pp × 11,320 ≈ 435 more visitors a month.
    const reachSuggestions = ref([
      withNl({
        id: 'rc1',
        idea: 'reach-visitors:paint-&-decor',
        zone: 'Paint & Decor',
        lever: 'Reach',
        leverKey: 'reach',
        visitors: '+435 visitors / mo',
        // Why it fires — the trigger, shown as a tag on the card (production fills
        // these from the ladder: current reach, Rung1_Reach, Rung1_ReachWobble).
        basis: { now: '15.2%', best: '19.0%', wobble: '1.5' },
        action:
          'Turn the aisle end that faces the main walkway into a colour-of-the-month wall — swatches, one finished sample board — so Paint & Decor registers from the route most shoppers already take.',
        why:
          'About 15% of your shoppers reach Paint & Decor; in its best weeks it was 19%. The aisle hasn\'t changed — fewer people are finding their way to it.',
        state: 'considering',
        confirming: null,
        pivotDraft: iso(TODAY),
        snoozeDraft: 14,
      }, {
        basis: { now: '15,2%', best: '19,0%', wobble: '1,5' },
        action:
          'Maak van de kop van het gangpad die naar het hoofdpad kijkt een wand met de kleur van de maand — kleurstalen, één afgewerkt voorbeeldbord — zodat Paint & Decor opvalt vanaf de route die de meeste bezoekers al nemen.',
        why:
          'Zo\'n 15% van je bezoekers komt in Paint & Decor; in de beste weken was dat 19%. Het gangpad is niet veranderd — er vinden gewoon minder mensen de weg ernaartoe.',
      }),
    ])
    const activeReachSuggestions = computed(() =>
      reachSuggestions.value.filter((s) => !['dismissed', 'snoozed', 'implemented'].includes(s.state)))


    // ---------------- Tracked actions (mock) ----------------
    // The seed list + its € math live in demo/guidanceDemo.js so the demo home's
    // "Value delivered" reads the very same actions (one source, never retyped).
    const tracked = ref(buildDemoTracked())

    // ?scenario=type_change (zone identity 6.3): the zone changed type today. Its
    // suggestions and snoozed ideas described it as its old type — gone until the nightly
    // run rebuilds them; an action in progress on it stays, with a line saying so.
    const _route = useRoute()
    const typeChanged = changeFor(_route.query)
    const moneyChanged = settingChanged(registerFor(_route.query), 'money')
    if (typeChanged) {
      const other = (x) => x && x.zone !== typeChanged.zone
      suggestions.value = suggestions.value.filter(other)
      serviceSuggestions.value = serviceSuggestions.value.filter(other)
      reachSuggestions.value = reachSuggestions.value.filter(other)
    }
    const _todayText = () => new Date().toLocaleDateString(demoIsNl() ? 'nl' : 'en-GB', { day: 'numeric', month: 'long' })
    const typeChangeLine = (a) => (typeChanged && a && a.scope === typeChanged.zone
      ? NOTICES.actionLine(_todayText(), ZONE_TYPE_NAMES[typeChanged.to]) : null)

    // ---------------- Helpers ----------------
    const TODAY_ISO = iso(TODAY)
    // elapsedDays / isVerdict come from demo/guidanceDemo.js


    // The pill: confidence first (too early = Trajectory), then — once there's
    // ~4 weeks on each side — the outcome itself. Direction uses the same ±4%
    // band as the delta chips.
    const verdictPill = (a) => {
      const pill = (key, cls) => ({
        key, cls, label: t(`demoActions.verdict.${key}`), hint: t(`demoActions.verdictHint.${key}`),
      })
      if (!isVerdict(a)) return pill('building_up', 'v-early')
      // Count wins vs. losses using each metric's good-direction, so a shorter
      // queue counts as a win even though the number went down. Informational
      // metrics (goodDir 0, e.g. wait rate) don't sway the verdict.
      let up = 0
      let down = 0
      for (const r of a.readings) {
        const gd = (METRIC[r.metric] || {}).goodDir || 0
        if (!gd || r.deltaPct == null) continue
        const eff = r.deltaPct * gd
        if (eff >= 4) up++
        else if (eff <= -4) down++
      }
      if (up && down) return pill('mixed', 'v-mixed')
      if (up) return pill('improved', 'v-improved')
      if (down) return pill('declined', 'v-declined')
      return pill('no_change', 'v-flat')
    }

    const fmtLong = (isoStr) => {
      const d = new Date(isoStr + 'T00:00:00')
      return `${d.getDate()} ${(demoIsNl() ? NL_MONTHS : MONTHS)[d.getMonth()]} ${d.getFullYear()}`
    }
    const fmtDelta = (p) => (p == null ? '—'
      : `${p > 0 ? '+' : ''}${demoIsNl() ? nlNum(p.toFixed(1)) : p.toFixed(1)}%`)
    const perDay = () => (demoIsNl() ? ' per dag' : ' per day')
    // Store traffic vs zone traffic, by scope ('' / 'Whole store' = the store; the
    // entrance counts store traffic too). Same naming as production GuidanceTab.
    const STORE_SCOPES = ['', 'Whole store', 'Main Entrance']
    const metricLabel = (m, scope = '') => {
      if (m === 'traffic') {
        return STORE_SCOPES.includes(scope || '')
          ? t('demoActions.metrics.storeTraffic') : t('demoActions.metrics.zoneTraffic')
      }
      return METRIC[m] ? t(`demoActions.metrics.${m}`) : m
    }
    const stateLabel = (s) =>
      s.state === 'considering' ? t('demoActions.state.considering') : s.state

    // Colour by whether the move is *good*, not by its sign: wait time −20% is
    // green, +20% is red. Informational metrics stay neutral.
    const deltaClass = (metric, p) => {
      if (p == null) return 'flat'
      const gd = (METRIC[metric] || {}).goodDir || 0
      if (!gd) return 'flat'
      const eff = p * gd
      if (eff >= 4) return 'up'
      if (eff <= -4) return 'down'
      return 'flat'
    }

    // A metric's LEVEL in its own unit: rates (reach, engagement, conversion) as a %,
    // traffic as visitors per day, times in seconds. Chips and the read sentence show
    // before → after levels rather than a % change, so a rate and a count never look
    // like the same kind of number. The % change still decides the chip's colour.
    const fmtLevel = (m, v) => {
      if (v == null) return '—'
      const meta = METRIC[m]
      if (!meta) return `${v}`
      if (m === 'traffic' || meta.axis === '{value}s') return fmtMetric(m, Math.round(v))
      return fmtMetric(m, Math.round(v * 10) / 10)
    }
    const levelPair = (a, r) => {
      const pts = (a.series || {})[r.metric]
      return [meanSide(pts, 'before'), meanSide(pts, 'after')]
    }
    const levelText = (a, r) => {
      const [b, af] = levelPair(a, r)
      if (b == null || af == null) return fmtDelta(r.deltaPct)
      return `${fmtLevel(r.metric, b)} → ${fmtLevel(r.metric, af)}${r.metric === 'traffic' ? perDay() : ''}`
    }

    const readSentence = (a) => (demoIsNl() ? readSentenceNl(a) : readSentenceEn(a))
    const readSentenceEn = (a) => {
      const parts = a.readings
        .filter((r) => r.deltaPct != null) // skip pending (e.g. conversion awaiting sales)
        .map((r) => {
          const label = metricLabel(r.metric, a.scope).toLowerCase()
          const [b] = levelPair(a, r)
          if (Math.abs(r.deltaPct) < 4) {
            return `<strong>${label} flat${b != null ? ` at ${fmtLevel(r.metric, b)}${r.metric === 'traffic' ? ' per day' : ''}` : ''}</strong>`
          }
          return `<strong>${label} ${levelText(a, r)}</strong>`
        })
      const weeks = Math.round(elapsedDays(a) / 7)
      const joined =
        parts.length > 1
          ? parts.slice(0, -1).join(', ') + ' and ' + parts.slice(-1)
          : parts[0]
      const tail = isVerdict(a)
        ? `vs. the prior ${weeks} weeks.`
        : `so far vs. the ${elapsedDays(a)} days before — still building up, not a verdict yet.`
      const who = a.scope || 'the store'
      return `Since ${fmtLong(a.pivot)}, in ${who}: ${joined} ${tail}`
    }
    // The same sentence in Dutch, with the same figures.
    const readSentenceNl = (a) => {
      const parts = a.readings
        .filter((r) => r.deltaPct != null)
        .map((r) => {
          const label = metricLabel(r.metric, a.scope).toLowerCase()
          const [b] = levelPair(a, r)
          if (Math.abs(r.deltaPct) < 4) {
            return `<strong>${label} stabiel${b != null ? ` op ${fmtLevel(r.metric, b)}${r.metric === 'traffic' ? perDay() : ''}` : ''}</strong>`
          }
          return `<strong>${label} ${levelText(a, r)}</strong>`
        })
      const weeks = Math.round(elapsedDays(a) / 7)
      const joined =
        parts.length > 1
          ? parts.slice(0, -1).join(', ') + ' en ' + parts.slice(-1)
          : parts[0]
      const tail = isVerdict(a)
        ? `t.o.v. de ${weeks} weken ervoor.`
        : `tot nu toe t.o.v. de ${elapsedDays(a)} dagen ervoor — er wordt nog data verzameld, nog geen eindoordeel.`
      const who = a.scope || 'de winkel'
      return `Sinds ${fmtLong(a.pivot)}, in ${who}: ${joined} ${tail}`
    }

    const chartOption = (a) => {
      const series = (a.series && a.series[a.focus]) || []
      const nlLabel = (isoStr) => {
        const d = new Date(isoStr + 'T00:00:00')
        return `${d.getDate()} ${NL_MONTHS[d.getMonth()]}`
      }
      const labels = series.map((p) => (demoIsNl() ? nlLabel(p.dateISO) : p.label))
      const values = series.map((p) => p.value)
      const pivotLabel = (() => {
        if (demoIsNl()) return nlLabel(a.pivot)
        const d = new Date(a.pivot + 'T00:00:00')
        return `${d.getDate()} ${MONTHS[d.getMonth()]}`
      })()
      const meta = METRIC[a.focus] || METRIC.traffic
      // Shade the "since" region by the outcome of the focused metric, not a
      // fixed green — a Declined action shouldn't look positive.
      const fr = a.readings.find((r) => r.metric === a.focus)
      const status = fr && fr.deltaPct != null ? deltaClass(a.focus, fr.deltaPct) : 'flat'
      const afterColor =
        status === 'up'
          ? 'rgba(178,193,73,0.16)'
          : status === 'down'
          ? 'rgba(176,70,75,0.12)'
          : 'rgba(148,163,184,0.12)'
      return {
        // No entry animation: the option is recomputed on any reactive change
        // (e.g. typing in the add-form), and with animation on the "change"
        // marker + line re-draw from scratch every time — looks like it's
        // "building up" again. Off = silent updates, instant metric switching.
        animation: false,
        grid: { left: 42, right: 16, top: 18, bottom: 46 },
        tooltip: {
          trigger: 'axis',
          valueFormatter: (v) => fmtMetric(a.focus in METRIC ? a.focus : 'traffic', v),
        },
        xAxis: {
          type: 'category',
          data: labels,
          axisLabel: { fontSize: 10, color: '#7a8a96', interval: Math.ceil(labels.length / 8) },
          axisLine: { lineStyle: { color: '#e6edf3' } },
        },
        yAxis: {
          type: 'value',
          axisLabel: { fontSize: 10, color: '#7a8a96',
            formatter: demoIsNl() && meta.axis === '{value}s' ? '{value} sec' : meta.axis },
          splitLine: { lineStyle: { color: '#f1f5f9' } },
        },
        series: [
          {
            type: 'line',
            data: values,
            smooth: true,
            symbol: 'none',
            lineStyle: { width: 2, color: '#4F7EB3' },
            areaStyle: { color: 'rgba(79,126,179,0.08)' },
            markArea: {
              silent: true,
              data: [
                [
                  { xAxis: labels[0], itemStyle: { color: 'rgba(148,163,184,0.10)' } },
                  { xAxis: pivotLabel },
                ],
                [
                  { xAxis: pivotLabel, itemStyle: { color: afterColor } },
                  { xAxis: labels[labels.length - 1] },
                ],
              ],
            },
            markLine: {
              silent: true,
              symbol: 'none',
              lineStyle: { color: '#B2C149', type: 'dashed', width: 2 },
              label: { formatter: t('demoActions.detail.change'), color: '#7a8a3f', fontSize: 10 },
              data: [{ xAxis: pivotLabel }],
            },
          },
        ],
      }
    }


    // finishImpact / computeImpact come from demo/guidanceDemo.js (shared with the demo home)
    // Texts in demoActions.basis.short / .full (measured, estimated, service).
    const BASIS_KEYS = ['measured', 'estimated', 'service']
    const basisShort = (b) => t(`demoActions.basis.short.${BASIS_KEYS.includes(b) ? b : 'estimated'}`)
    const basisFull = (b) => (BASIS_KEYS.includes(b) ? t(`demoActions.basis.full.${b}`) : '')

    // Everything the template needs to render the € line for one action.
    const impactView = (a) => {
      const imp = computeImpact(a)
      if (imp.confidence === 'na')
        return { kind: 'na', cls: 'imp-na' }
      if (imp.confidence === 'none')
        return { kind: 'none', cls: 'imp-none' }
      const neg = imp.eur < 0
      const cls = neg
        ? 'imp-bad'
        : imp.confidence === 'good'
        ? 'imp-good'
        : imp.confidence === 'early'
        ? 'imp-early'
        : 'imp-indicative'
      const value =
        imp.confidence === 'early'
          ? `${neg ? '−' : ''}${eurAbs(imp.low)}–${eurAbs(imp.high)}`
          : eurSigned(imp.eur)
      return {
        kind: 'value',
        cls,
        early: imp.confidence === 'early',
        value,
        sub: basisShort(imp.basis),
        tip: basisFull(imp.basis) +
          (imp.confidence === 'early' ? ' ' + t('demoActions.impact.early') : ''),
      }
    }

    // Roll-up across tracked actions. Measured (from sales) is the headline number;
    // modelled is kept SEPARATE so we never pass estimates off as banked revenue.
    const rollup = computed(() => {
      let measured = 0
      let modelled = 0
      let building = 0
      let unquantified = 0
      for (const a of tracked.value) {
        const imp = computeImpact(a)
        if (imp.confidence === 'good') measured += imp.eur
        else if (imp.confidence === 'indicative') modelled += imp.eur
        else if (imp.confidence === 'early') building += 1
        else unquantified += 1
      }
      return { measured, modelled, building, unquantified, hasNumbers: measured !== 0 || modelled !== 0 }
    })
    const eurSignedFmt = (n) => eurSigned(n)

    // ---------------- Lifetime value delivered (cumulative €) ----------------
    // The roll-up above is a €/MONTH run-rate. This is total € EARNED so far:
    // daily impact × days elapsed = the measured gain over the whole after-window
    // (not an extrapolation — we measured that window). Concluding an action as
    // "worked" banks its earnings so they persist after it leaves the tracker.
    const banked = reactive({ measured: 0, modelled: 0, since: null })
    const cumImpact = (a) => {
      const imp = computeImpact(a)
      const cum = ((imp.eur || 0) * elapsedDays(a)) / 30
      if (imp.confidence === 'good') return { measured: cum, modelled: 0 }
      if (imp.confidence === 'indicative') return { measured: 0, modelled: cum }
      return { measured: 0, modelled: 0 }
    }
    const earned = computed(() => {
      let m = banked.measured
      let md = banked.modelled
      let since = banked.since
      for (const a of tracked.value) {
        const c = cumImpact(a)
        m += c.measured
        md += c.modelled
        if ((c.measured || c.modelled) && (!since || a.pivot < since)) since = a.pivot
      }
      return { measured: m, modelled: md, since, has: m !== 0 || md !== 0 }
    })

    // ---------------- Interactions ----------------
    const setState = (s, state) => {
      s.state = state
      s.confirming = null
    }
    const dismiss = (s) => {
      s.state = 'dismissed'
    }
    const snooze = (s) => {
      s.state = 'snoozed'
      s.confirming = null
    }
    const startImplement = (s) => {
      s.confirming = 'implement'
    }

    // What each lever wants to watch, in priority order. Intersected with the
    // zone's measurable metrics so we never promise a number we can't ground.
    const LEVER_METRICS = {
      waiting: ['wait', 'wait_rate', 'traffic'],
      engagement: ['engagement', 'dwell', 'traffic'],
      conversion: ['conversion', 'dwell', 'traffic'],
      // Reach = getting more of the store's shoppers TO the zone. Judged on reach
      // (zone ÷ store traffic), never on engagement or dwell: a reach change that
      // works usually brings in more casual visitors and dilutes those. Zone traffic
      // sits beside it to show why reach moved.
      reach: ['reach', 'traffic'],
      // Staffing: unhelped customers in the zone / the checkout queue wait.
      svc: ['service_response', 'traffic'],
      chk: ['wait', 'wait_rate', 'traffic'],
    }
    const implement = (s) => {
      const pivot = s.pivotDraft || TODAY_ISO
      const caps = capsFor(s.zone)
      const want = LEVER_METRICS[s.leverKey] || ['traffic', 'dwell']
      const chosen = want.filter((m) => caps.includes(m))
      tracked.value.unshift(
        freshTracked('new-' + s.id, s.zone, s.action, pivot, chosen.length ? chosen : caps, s.idea,
          s.visitors ? 'reach' : (['svc', 'chk'].includes(s.leverKey) ? 'waiting' : 'money'))
      )
      s.state = 'implemented'
      s.confirming = null
    }

    // ---------------- Add an action manually ----------------
    const showAddForm = ref(false)
    // The demo store's zones (same bundle the nav uses) — the form offers each place
    // only what it can measure, from its own configuration.
    const demoZones = ref([])
    loadDemoJSON('/demo_data/demo_detection_zones.json')
      .then((j) => { demoZones.value = (j && j.detectionzones) || [] })
      .catch(() => { demoZones.value = [] })
    const scopeOptions = [
      'Whole store',
      'Main Entrance',
      'Power Tools',
      'Safety Shoes',
      'Garden Utilities',
      'Promoted Articles Zone',
      'Paint & Decor',
      'Checkout 1 Waiting',
      'Checkout 1 Interaction',
      'Customer Service Desk',
    ]
    // `watch` holds a boolean per metric, but only the current scope's metrics
    // are ever shown — so the checkboxes are always the ones that scope can
    // actually measure (wait/wait-rate for a queue, engagement/conversion for a
    // product bay), never a wall of greyed-out "n/a".
    const newAction = reactive({ what: '', scope: 'Whole store', pivot: TODAY_ISO, watch: {} })
    const newCaps = computed(() => capsFor(newAction.scope))
    const resetWatch = () => {
      const caps = capsFor(newAction.scope)
      const w = {}
      caps.forEach((m, i) => {
        w[m] = i === 0 // default: the primary lever on
      })
      newAction.watch = w
    }
    resetWatch()
    watch(() => newAction.scope, resetWatch)
    const canAdd = computed(
      () => newAction.what.trim().length > 0 && newCaps.value.some((m) => newAction.watch[m])
    )
    let addCounter = 0
    const addAction = () => {
      if (!canAdd.value) return
      addCounter += 1
      const chosen = newCaps.value.filter((m) => newAction.watch[m])
      tracked.value.unshift(
        freshTracked('man-' + addCounter, newAction.scope, newAction.what.trim(), newAction.pivot || TODAY_ISO, chosen, null, 'own')
      )
      newAction.what = ''
      newAction.scope = 'Whole store'
      newAction.pivot = TODAY_ISO
      resetWatch()
      showAddForm.value = false
    }

    // ---------------- Per-action sales (POS surrogate) ----------------
    // Sales live on the action that watches conversion — not every change needs
    // them. Feeding sales is what turns conversion from "pending" into a measured
    // lever that can move the verdict.
    const toggleSales = (a) => {
      if (!a.saleDraft) a.saleDraft = { date: TODAY_ISO, transactions: null, revenue: null }
      if (!a.salesLog) a.salesLog = []
      a.showSales = !a.showSales
    }
    const saveActionSale = (a) => {
      const t = Number(a.saleDraft.transactions)
      if (!(t > 0)) return
      a.salesLog.unshift({
        date: a.saleDraft.date,
        transactions: t,
        revenue: a.saleDraft.revenue ? Number(a.saleDraft.revenue) : null,
      })
      a.saleDraft.transactions = null
      a.saleDraft.revenue = null
      if (!a.salesProvided) {
        // First entry unlocks the sales-based metric(s) — measured from here,
        // flat until the window fills (same day-one honesty as the others).
        a.salesProvided = true
        // An action that wasn't watching one (e.g. a reach change) starts measuring
        // the zone's conversion — or the store's purchase rate store-wide.
        if (!a.readings.some((r) => SALES_METRICS.includes(r.metric))) {
          const m = capsFor(a.scope).includes('conversion') ? 'conversion' : 'purchase_rate'
          a.readings.push({ metric: m, deltaPct: 0.0 })
          a.series = { ...a.series,
            ...seriesSet(a.pivot, elapsedDays(a), { [m]: [BASE_MEAN[m], BASE_MEAN[m]] }, 7000) }
        }
        a.readings.forEach((r) => {
          if (SALES_METRICS.includes(r.metric)) {
            r.pending = false
            r.deltaPct = 0.0
          }
        })
      }
    }

    // The sales-based metric a card is waiting on, and its honest definition —
    // conversion (÷ engaged) vs purchase rate (÷ store visitors) are different.
    const salesDef = (a) =>
      a.readings.some((r) => r.metric === 'purchase_rate')
        ? t('demoActions.sales.defPurchase')
        : t('demoActions.sales.defConversion')

    // ---------------- Conclude an action → learning ledger ----------------
    const concluded = ref([
      withNl({
        // Matches sg3 (Safety Shoes / size-run-aisle-edge) → that suggestion is
        // shown flagged "tried before", not suppressed.
        id: 'cl-seed-exec',
        idea: 'size-run-aisle-edge',
        scope: 'Safety Shoes',
        what: 'Brought the size run to the aisle edge with a stock sign.',
        pivot: demoDateStr(98),
        measuredUntil: demoDateStr(66),
        verdict: 'declined',
        attribution: 'exec',
        note: 'The display was only half-stocked and the sign fell down after a few days — the idea never really got a fair run.',
      }, {
        what: 'De volledige maatreeks met een voorraadbord naar de rand van het gangpad gebracht.',
        note: 'De display was maar half gevuld en het bord viel na een paar dagen om — het idee heeft nooit echt een eerlijke kans gekregen.',
      }),
      withNl({
        // Matches sg5 (Garden Utilities / garden-banner) → that suggestion is
        // held back and won't show in the active inbox.
        id: 'cl-seed-drop',
        idea: 'garden-banner',
        scope: 'Garden Utilities',
        what: 'Hung a seasonal banner over the garden aisle.',
        pivot: demoDateStr(82),
        measuredUntil: demoDateStr(50),
        verdict: 'no_change',
        attribution: 'drop',
        note: 'Ran a full month, no movement — our shoppers come for a specific job, overhead signage just doesn’t register.',
      }, {
        what: 'Een seizoensbanner boven het tuingangpad gehangen.',
        note: 'Een volle maand laten hangen, geen beweging — onze klanten komen voor een specifieke klus, een bord boven het pad valt hen gewoon niet op.',
      }),
    ])
    // An attribution with its texts in the viewer's language.
    const attrText = (a) => ({
      ...a,
      label: t(`demoActions.attr.${a.key}.label`),
      short: t(`demoActions.attr.${a.key}.short`),
      allen: t(`demoActions.attr.${a.key}.allen`),
    })
    const attrMeta = (key) => attrText(ATTR_BY_KEY[key] || ATTR_BY_KEY.inconclusive)
    const attributions = computed(() => ATTRIBUTIONS.map(attrText))
    const verdictClsFor = (key) => VERDICT_CLS[key] || 'v-flat'
    const verdictLabelFor = (key) => (VERDICT_LABEL[key] ? t(`demoActions.verdict.${key}`) : key)

    // --- Close the loop: the inbox reads the learning ledger by (idea, zone) ---
    const outcomeFor = (s) =>
      concluded.value.find(
        (c) => c.idea && c.idea === s.idea && (c.scope || '') === (s.zone || '')
      )
    const isHeldBack = (s) => !s.reconsidered && outcomeFor(s)?.attribution === 'drop'
    const execFlagFor = (s) => {
      const o = outcomeFor(s)
      return o && o.attribution === 'exec' ? o : null
    }
    // Biggest opportunity first — the € headline drives the order, so a small
    // checkout/entrance item never sits above a large product-zone gap.
    const eurNum = (s) => Number(String(s.eur || '').replace(/[^0-9]/g, '')) || 0
    const activeSuggestions = computed(() =>
      openSuggestions.value.filter((s) => !isHeldBack(s)).sort((a, b) => eurNum(b) - eurNum(a))
    )
    const heldBackSuggestions = computed(() => openSuggestions.value.filter((s) => isHeldBack(s)))

    // Same suppression rules, no € sort — these carry no euro to rank by.
    const activeServiceSuggestions = computed(() =>
      serviceSuggestions.value.filter((s) => s.state !== 'dismissed' && s.state !== 'snoozed'))
    // Every open suggestion across the three groups — the spine's "Open" count and
    // the section's empty state.
    // ---- Kind of an action: which Open group it came from ----
    // Production derives it from the action's `idea` key (svc:/chk: → waiting,
    // reach-visitors: → more shoppers, any other suggestion → money, none → your own
    // idea), so it needs no schema change. The demo also sets it explicitly.
    const KIND_LABEL = computed(() => ({
      money: t('demoActions.kinds.money'), waiting: t('demoActions.kinds.waiting'),
      reach: t('demoActions.kinds.reach'), own: t('demoActions.kinds.own'),
    }))
    const KIND_ORDER = ['money', 'waiting', 'reach', 'own']
    const kindOf = (a) => {
      if (a && a.kind) return a.kind
      const idea = (a && a.idea) || ''
      if (!idea) return 'own'
      if (/^(svc|chk):/.test(idea)) return 'waiting'
      if (/^reach-visitors:/.test(idea)) return 'reach'
      return 'money'
    }
    const byKind = (arr) =>
      [...arr].sort((x, y) => KIND_ORDER.indexOf(kindOf(x)) - KIND_ORDER.indexOf(kindOf(y)))
    const openCount = computed(() =>
      activeSuggestions.value.length + activeServiceSuggestions.value.length
      + activeReachSuggestions.value.length)

    // ---------------- Page layout: one lifecycle stage at a time ----------------
    // The lifecycle tabs are the navigation; within a stage, quiet filter pills
    // replace boxed groups, and each card shows the essentials with the rest one
    // click away.
    // `?stage=progress|done` deep-links straight to a stage (e.g. from a notification).
    const _qStage = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('stage') : null
    const stage = ref(['open', 'progress', 'done'].includes(_qStage) ? _qStage : 'open')
    const openFilter = ref('all')
    const progressFilter = ref('all')
    const expanded = reactive({})
    const toggleExpanded = (id) => { expanded[id] = !expanded[id] }
    const markConsidering = (s) => { setState(s, 'considering'); s.pinned = true }

    const stages = computed(() => [
      { key: 'open', label: t('demoActions.stages.open'), hint: t('demoActions.stages.openHint'), count: openCount.value },
      { key: 'progress', label: t('demoActions.stages.progress'), hint: t('demoActions.stages.progressHint'), count: tracked.value.length },
      { key: 'done', label: t('demoActions.stages.done'), hint: t('demoActions.stages.doneHint'), count: concluded.value.length },
    ])

    // Open: one list, money → waiting → more shoppers; each group keeps its own order
    // (€ can't be ranked against visitors, and staffing carries no figure at all).
    const openItems = computed(() => [
      ...activeSuggestions.value.map((s) => ({ s, kind: 'money' })),
      ...activeServiceSuggestions.value.map((s) => ({ s, kind: 'waiting' })),
      ...activeReachSuggestions.value.map((s) => ({ s, kind: 'reach' })),
    ])
    const openFilters = computed(() => [
      { key: 'all', label: t('demoActions.filters.all'), count: openItems.value.length },
      ...['money', 'waiting', 'reach'].map((k) => ({
        key: k, label: KIND_LABEL.value[k], count: openItems.value.filter((i) => i.kind === k).length,
      })),
    ])
    const visibleOpen = computed(() => (openFilter.value === 'all'
      ? openItems.value : openItems.value.filter((i) => i.kind === openFilter.value)))
    // The card's headline figure, in its own unit.
    const suggValue = (it) => {
      if (it.kind === 'money') return { main: String(it.s.eur || '').replace(/\s*\/\s*mo$/, ''), unit: t('demoActions.open.perMonth') }
      if (it.kind === 'reach') return { main: String(it.s.visitors || '').replace(/\s*visitors.*$/, ''), unit: t('demoActions.open.visitorsPerMonth') }
      return { main: null, unit: t('demoActions.open.staffingPattern') }
    }

    // In progress: ready-for-a-verdict first, each ordered by kind.
    const progressFilters = computed(() => [
      { key: 'all', label: t('demoActions.filters.all'), count: tracked.value.length },
      { key: 'ready', label: t('demoActions.filters.ready'), count: tracked.value.filter(isVerdict).length },
      { key: 'measuring', label: t('demoActions.filters.measuring'), count: tracked.value.filter((a) => !isVerdict(a)).length },
    ])
    const visibleTracked = computed(() => {
      const ready = byKind(tracked.value.filter(isVerdict))
      const measuring = byKind(tracked.value.filter((a) => !isVerdict(a)))
      if (progressFilter.value === 'ready') return ready
      if (progressFilter.value === 'measuring') return measuring
      return [...ready, ...measuring]
    })
    // The row's headline is the action's GOAL metric — what it was meant to move —
    // never a fallback. If the goal can't be read yet (conversion without sales), the
    // row says so instead of quietly showing another metric.
    const goalOf = (a) => a.goal || ((a.readings || [])[0] || {}).metric
    const headlineReading = (a) => {
      const g = goalOf(a)
      const r = (a.readings || []).find((x) => x.metric === g)
      if (!r) return null
      const label = t('demoActions.progress.goal', { metric: metricLabel(r.metric, a.scope) })
      if (r.pending) return { label, text: t('demoActions.progress.needsSales'), cls: 'rd-pending' }
      return { label, text: levelText(a, r), cls: 'rd-' + deltaClass(r.metric, r.deltaPct) }
    }
    // "No sales added": this action's € could be MEASURED from sales but none are
    // in, so any € shown is only estimated (modelled conversion × basket). Staffing
    // actions are excluded — their € comes from the queue model, sales don't change it.
    const noSalesAdded = (a) => kindOf(a) !== 'waiting' && !a.salesProvided
    const measureProgress = (a) => Math.min(100, Math.round((elapsedDays(a) / 28) * 100))
    const reconsider = (s) => {
      s.reconsidered = true // manual override — surface it again despite the drop
    }

    const startConclude = (a) => {
      if (!a.concludeAttr) a.concludeAttr = verdictPill(a).key === 'improved' ? 'worked' : 'inconclusive'
      if (a.concludeNote == null) a.concludeNote = ''
      a.concluding = true
    }
    const cancelConclude = (a) => {
      a.concluding = false
    }
    const confirmConclude = (a) => {
      // Worked → bank its earnings-to-date so they survive leaving the tracker.
      if (a.concludeAttr === 'worked') {
        const c = cumImpact(a)
        banked.measured += c.measured
        banked.modelled += c.modelled
        if ((c.measured || c.modelled) && (!banked.since || a.pivot < banked.since)) banked.since = a.pivot
      }
      concluded.value.unshift({
        id: 'cl-' + a.id,
        idea: a.idea || null,
        kind: kindOf(a),
        scope: a.scope,
        what: a.what,
        pivot: a.pivot,
        measuredUntil: demoDateStr(0),
        verdict: verdictPill(a).key,
        attribution: a.concludeAttr,
        note: (a.concludeNote || '').trim(),
      })
      tracked.value = tracked.value.filter((x) => x.id !== a.id)
    }

    // Deep-link from Today's "Act on this →": scroll to the incoming zone's card
    // and flash it once, so the same opportunity continues visually into Actions.
    const rootEl = ref(null)
    const flashZone = ref('')
    let _flashedFor = null
    const focusOnZone = () => {
      const z = props.focusZone
      if (!z || _flashedFor === z) return
      stage.value = 'open'
      openFilter.value = 'all'
      nextTick(() => {
        const root = rootEl.value
        if (!root) return
        const sel = (window.CSS && CSS.escape) ? CSS.escape(z) : z
        const el = root.querySelector(`[data-zone="${sel}"]`)
        if (!el) return
        _flashedFor = z
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        flashZone.value = z
        setTimeout(() => { if (flashZone.value === z) flashZone.value = '' }, 2200)
      })
    }
    watch([() => props.focusZone, activeSuggestions], focusOnZone, { immediate: true })

    // #2 forward weather heads-up (demo). Production composes this from the nightly
    // outlook (weather_signature.outlook) for weather-led stores; null = nothing shown.
    // `validThrough` = the last forecast day it covers → it auto-expires after that
    // even if a nightly run is missed, so a stale tip never lingers past the event.
    const weatherHeadsUp = ref(withNl({
      title: 'Warm, dry weekend ahead',
      text: "Garden Utilities reacts strongly to warm weather, so expect it busier than a normal weekend. Make sure it's well-stocked and think about an extra staff-hour on Saturday afternoon.",
      validThrough: demoDateStr(0),   // the Sunday it covers (5 days after demo TODAY)
    }, {
      title: 'Warm, droog weekend op komst',
      text: 'Garden Utilities reageert sterk op warm weer, dus reken op meer drukte dan in een gewoon weekend. Zorg dat alles goed aangevuld is en denk aan een extra uur bezetting op zaterdagmiddag.',
    }))
    const weatherHeadsUpDismissed = ref(false)
    // Time-sensitive: self-expires once the window passes (belt to the nightly recompute
    // that clears it at source), and hidden early if the user taps "Noted".
    const showWeatherHeadsUp = computed(() => {
      const h = weatherHeadsUp.value
      if (!h || weatherHeadsUpDismissed.value) return false
      if (h.validThrough && TODAY > new Date(`${h.validThrough}T23:59:59`)) return false
      return true
    })

    return {
      sayDay,
      rootEl, flashZone,
      weatherHeadsUp, weatherHeadsUpDismissed, showWeatherHeadsUp,
      TODAY_ISO,
      suggestions,
      serviceSuggestions,
      activeServiceSuggestions,
      activeReachSuggestions,
      openCount,
      stage,
      openFilter,
      progressFilter,
      expanded,
      toggleExpanded,
      markConsidering,
      stages,
      openItems,
      openFilters,
      visibleOpen,
      suggValue,
      progressFilters,
      visibleTracked,
      headlineReading,
      measureProgress,
      noSalesAdded,
      KIND_LABEL,
      kindOf,
      activeSuggestions,
      heldBackSuggestions,
      execFlagFor,
      reconsider,
      tracked,
      elapsedDays,
      isVerdict,
      verdictPill,
      fmtLong,
      fmtDelta,
      metricLabel,
      stateLabel,
      deltaClass,
      readSentence,
      chartOption,
      levelText,
      impactView,
      rollup,
      earned,
      eurSignedFmt,
      setState,
      dismiss,
      snooze,
      startImplement,
      implement,
      concluded,
      typeChangeLine, moneyChanged, NOTICES,
      attributions,
      eurPlain,
      t,
      attrMeta,
      verdictClsFor,
      verdictLabelFor,
      startConclude,
      cancelConclude,
      confirmConclude,
      showAddForm,
      demoZones,
      scopeOptions,
      newAction,
      newCaps,
      canAdd,
      addAction,
      toggleSales,
      saveActionSale,
      salesDef,
    }
  },
})
</script>

<style scoped>
/* =====================================================================
   Actions — one lifecycle stage at a time
   Calm by default: white cards on the page, one meta line, one figure, one
   primary button. Kind is a coloured dot + word (never a coloured box); detail
   opens on demand. Colours: kind = the existing lever palette; accent = brand.
   ===================================================================== */
.act {
  --ink: #1d2b3a;
  --body: #425461;
  --muted: #6b7a87;
  --faint: #93a1ad;
  --line: #e6edf3;
  --line-strong: #d3dde7;
  --accent: var(--stora-accent, #4F7EB3);
  max-width: 1100px;
  /* Responsive to ITS OWN width, not the screen's: on a tablet the sidebar stays
     open, so this area can be ~440px on an 820px screen. */
  container-type: inline-size;
  container-name: act;
}
.kind-money   { --k: #2c7a3f; }
.kind-waiting { --k: #0D9488; }
.kind-reach   { --k: #5b53a6; }
.kind-own     { --k: #7d8c99; }

/* Header */
.act-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px 24px;
  flex-wrap: wrap;
  margin: 4px 0 18px;
}
.act-title { margin: 0; font-size: 1.5rem; font-weight: 800; color: var(--ink); letter-spacing: -0.4px; }
.act-sub { margin: 4px 0 0; color: var(--muted); font-size: 0.92rem; }
.act-earned { display: flex; flex-direction: column; align-items: flex-end; text-align: right; }
.act-earned-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--muted);
}
.act-earned-label .bi { color: #c69a2b; }
.act-earned-value { font-size: 1.5rem; font-weight: 800; color: #2c7a3f; line-height: 1.15; letter-spacing: -0.4px; }
.act-earned-note { font-size: 0.76rem; color: var(--faint); }

/* Lifecycle tabs */
.stage-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 22px;
  border-bottom: 1px solid var(--line);
}
.btn-primary-sm.stage-bar-btn {
  margin: 4px 0;
  padding: 5px 11px;
  font-size: 0.78rem;
  gap: 5px;
}
.stage-tabs {
  display: flex;
  align-items: stretch;
  gap: 4px;
  flex-wrap: wrap;
  align-self: flex-end;
}
.stage-tab {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: -1px;
  padding: 10px 14px 12px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  border-radius: 10px 10px 0 0;
  color: var(--muted);
  font: inherit;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.stage-tab:hover { background: #f5f8fb; color: var(--ink); }
.stage-tab.active { border-bottom-color: var(--accent); color: var(--ink); }
.stage-tab:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
.stage-count {
  min-width: 30px;
  height: 30px;
  padding: 0 9px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: #eef2f6;
  color: var(--body);
  font-size: 0.95rem;
  font-weight: 800;
}
.stage-tab.active .stage-count { background: var(--accent); color: #fff; }
.stage-text { display: flex; flex-direction: column; text-align: left; line-height: 1.2; }
.stage-label { font-size: 0.95rem; font-weight: 700; }
.stage-hint { font-size: 0.76rem; color: var(--faint); }
.stage-arrow { display: flex; align-items: center; color: #c8d1da; font-size: 0.8rem; }
@container act (max-width: 640px) {
  /* Tabs: one even row (count + name), the button full width beneath it. */
  .act .stage-bar { border-bottom: none; gap: 10px; }
  .act .stage-tabs { width: 100%; flex-wrap: nowrap; gap: 0; border-bottom: 1px solid var(--line); }
  .act .stage-hint, .act .stage-arrow { display: none; }
  .act .stage-tab { flex: 1 1 0; justify-content: center; gap: 7px; padding: 8px 4px 10px; min-width: 0; }
  .act .stage-count { min-width: 24px; height: 24px; padding: 0 7px; font-size: 0.82rem; }
  .act .stage-label { font-size: 0.86rem; white-space: nowrap; }
  .act .btn-primary-sm.stage-bar-btn { width: 100%; justify-content: center; margin: 0; padding: 8px 12px; }
  /* Filter pills: one row you swipe, never three lines. */
  .act .filters { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; padding-bottom: 2px; width: 100%; }
  .act .filters::-webkit-scrollbar { display: none; }
  .act .filter { flex: 0 0 auto; }
  /* A soft fade at the right edge says "there's more — swipe"; the end padding lets
     the last pill scroll fully clear of it. */
  .act .filters {
    padding-right: 28px;
    -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 28px), transparent);
    mask-image: linear-gradient(to right, #000 calc(100% - 28px), transparent);
  }
  /* Weather tip: text full width, "Noted" beneath it. */
  .act .notice { flex-wrap: wrap; align-items: flex-start; }
  .act .notice-body { flex: 1 1 calc(100% - 32px); }
  .act .notice .link-btn { margin-left: auto; }
  /* Header: the € figure under the title, left-aligned. */
  .act .act-earned { align-items: flex-start; text-align: left; }
}

.stage-intro { margin: 0 0 16px; max-width: 680px; color: var(--body); font-size: 0.9rem; line-height: 1.5; }
.empty-state { padding: 28px 0; color: var(--muted); font-style: italic; }

/* Weather heads-up */
.notice {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #fbf7ec;
  color: #5c4a1c;
  font-size: 0.88rem;
}
.notice-ic { font-size: 1.1rem; color: #b8892d; }
.notice-body { flex: 1; min-width: 0; line-height: 1.45; }

/* Toolbar + filter pills */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.filters { display: flex; flex-wrap: wrap; gap: 8px; }
.filter {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 999px;
  color: var(--body);
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.filter:hover:not(:disabled) { border-color: var(--line-strong); }
.filter.active { background: var(--ink); border-color: var(--ink); color: #fff; }
.filter:disabled { opacity: 0.45; cursor: default; }
.filter:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.filter-n { color: var(--faint); font-weight: 700; }
.filter.active .filter-n { color: rgba(255, 255, 255, 0.7); }

.dot { flex: 0 0 auto; width: 8px; height: 8px; border-radius: 50%; background: var(--k, var(--faint)); }

/* One meta line, shared by every list: kind · zone · tags */
.meta { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; font-size: 0.8rem; }
.meta-kind { color: var(--k); font-weight: 700; }
.meta-sep { color: #c8d1da; }
.meta-zone { color: var(--ink); font-weight: 700; }
.meta-lever { color: var(--muted); font-weight: 600; }
.meta-lever::before { content: '·'; margin-right: 6px; color: #c8d1da; }
.meta-date { margin-left: 2px; color: var(--faint); }
.meta-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #f1f4f7;
  color: var(--muted);
  font-size: 0.74rem;
  font-weight: 600;
  cursor: default;
}
.meta-tag.tag-warn { background: #fdf4e3; color: #9a6b12; }
.meta-tag.tag-nomoney { background: #fff6e5; color: #9a6b12; cursor: help; }

/* Lists */
.sugg-list, .track-list, .learned-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* Open: suggestion card */
.sugg {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px 28px;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 14px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.sugg:hover { border-color: var(--line-strong); box-shadow: 0 4px 16px rgba(13, 38, 76, 0.06); }
.sugg.flash { animation: suggFlash 2.2s ease-out; }
.sugg-main { min-width: 0; }
.sugg-action { max-width: 66ch; margin: 8px 0 6px; color: var(--ink); font-size: 0.97rem; line-height: 1.5; }
.why-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  background: none;
  border: none;
  color: var(--accent);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
}
.sugg-why {
  max-width: 66ch;
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f6f8fb;
  color: var(--body);
  font-size: 0.88rem;
  line-height: 1.5;
}
.sugg-why p { margin: 0; }
.sugg-why .sugg-why-note { margin-top: 6px; color: #9a6b12; }
.sugg-side { display: flex; flex-direction: column; align-items: flex-end; min-width: 150px; text-align: right; }
.value { color: var(--k); font-size: 1.4rem; font-weight: 800; line-height: 1.1; letter-spacing: -0.3px; }
.value.value-muted { font-size: 1.2rem; }
.value-unit { margin-top: 2px; color: var(--faint); font-size: 0.76rem; }
.sugg-buttons {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
}
.inline-form {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f6f8fb;
  color: var(--body);
  font-size: 0.86rem;
}
.inline-form label { display: flex; align-items: center; gap: 8px; font-weight: 600; }
.inline-form input, .inline-form select {
  padding: 5px 8px;
  border: 1px solid var(--line-strong);
  border-radius: 8px;
  font: inherit;
}
@container act (max-width: 680px) {
  .act .sugg { grid-template-columns: minmax(0, 1fr); gap: 6px; padding: 16px; }
  /* Flatten the text column so the figure can sit right under the zone line. */
  .act .sugg-main { display: contents; }
  .act .sugg .meta { order: 1; }
  .act .sugg-side { order: 2; flex-direction: row; align-items: baseline; gap: 8px; min-width: 0; text-align: left; }
  .act .sugg-action { order: 3; margin: 2px 0; }
  .act .why-toggle { order: 4; justify-self: start; }
  .act .sugg-why { order: 5; }
  .act .sugg-buttons { order: 6; }
  .act .inline-form { order: 7; }
  .act .value { font-size: 1.2rem; }
  .act .sugg-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding-top: 8px;
  }
  .act .sugg-buttons > :first-child, .act .sugg-buttons > :last-child { grid-column: 1 / -1; }
  .act .sugg-buttons > *, .act .sugg-buttons button { width: 100%; justify-content: center; }
  .act .inline-form { align-items: stretch; }
  .act .inline-form label { flex-wrap: wrap; width: 100%; }
}

/* Buttons */
.btn-primary-sm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: var(--accent);
  border: none;
  border-radius: 999px;
  color: #fff;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.btn-primary-sm:hover { filter: brightness(1.08); }
.btn-primary-sm:disabled { opacity: 0.5; cursor: default; }
.btn-ghost-sm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 999px;
  color: var(--ink);
  font: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
}
.btn-ghost-sm:hover { border-color: var(--line-strong); }
.btn-ghost-sm.on { background: #f1f4f7; border-color: var(--line-strong); }
.btn-ghost-sm.danger { color: #b3372f; }
/* Demo-locked: visible so the feature is on show, not usable. */
.is-locked, .is-locked:hover,
.btn-ghost-sm.danger.is-locked, .btn-ghost-sm.danger.is-locked:hover {
  background: #f6f8fa;
  border-color: var(--line);
  color: var(--faint);
  cursor: not-allowed;
  text-decoration: none;
}
.is-locked .lock-ic { font-size: 0.72rem; }
.btn-ghost-sm.danger:hover { border-color: #e6b8b4; background: #fdf5f4; }
.icon-btn {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 50%;
  color: var(--muted);
  cursor: pointer;
}
.icon-btn:hover { border-color: var(--line-strong); color: var(--ink); }
.link-btn {
  padding: 0 2px;
  background: none;
  border: none;
  color: var(--muted);
  font: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
}
.link-btn:hover { color: var(--ink); text-decoration: underline; }
.btn-primary-sm:focus-visible, .btn-ghost-sm:focus-visible, .icon-btn:focus-visible,
.link-btn:focus-visible, .why-toggle:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* Held back — quiet disclosure */
.held { margin-top: 18px; color: var(--muted); font-size: 0.86rem; }
/* Looks and behaves like a button: bordered pill, explicit "Show"/"Hide", chevron. */
.held-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 999px;
  color: var(--body);
  font-weight: 600;
  cursor: pointer;
  list-style: none;
  transition: border-color 0.15s ease;
}
.held-toggle::-webkit-details-marker { display: none; }
.held-toggle:hover { border-color: var(--line-strong); }
.held-toggle:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.held-cta { display: inline-flex; align-items: center; gap: 4px; color: var(--accent); }
.held-cta .bi { transition: transform 0.15s ease; }
.held-hide { display: none; }
.held[open] .held-show { display: none; }
.held[open] .held-hide { display: inline; }
.held[open] .held-cta .bi { transform: rotate(180deg); }
.held[open] .held-toggle { margin-bottom: 6px; }
.held-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
}
.held-reason { margin-top: 2px; color: var(--faint); font-size: 0.8rem; }

/* In progress: tracked impact + rows */
.summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-bottom: 16px;
  padding: 14px 18px;
  background: #f4faf5;
  border: 1px solid #d9eedf;
  border-radius: 12px;
}
.summary-main { display: flex; align-items: baseline; flex-wrap: wrap; gap: 10px; }
.summary-label {
  color: var(--muted);
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.summary-value { color: #2c7a3f; font-size: 1.35rem; font-weight: 800; }
.summary-per { color: var(--muted); font-size: 0.85rem; font-weight: 600; }
.summary-tag { color: var(--muted); font-size: 0.76rem; cursor: help; }
.summary-extra { display: flex; flex-wrap: wrap; gap: 14px; color: var(--muted); font-size: 0.8rem; }

.track {
  overflow: hidden;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 14px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.track:hover, .track.open { border-color: var(--line-strong); box-shadow: 0 4px 16px rgba(13, 38, 76, 0.06); }
.track-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 150px 16px;
  align-items: center;
  gap: 20px;
  width: 100%;
  padding: 14px 18px;
  background: none;
  border: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.track-row:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
.track-id { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.track-what {
  display: -webkit-box;
  overflow: hidden;
  color: var(--body);
  font-size: 0.9rem;
  line-height: 1.4;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.track-reading { display: flex; flex-direction: column; align-items: flex-end; text-align: right; }
.track-reading-label { color: var(--faint); font-size: 0.74rem; }
.track-reading-value { color: var(--ink); font-size: 0.92rem; font-weight: 700; white-space: nowrap; }
.track-reading-value.rd-up { color: #2c7a3f; }
.track-reading-value.rd-down { color: #b3372f; }
.track-reading-value.rd-pending { color: #9a6b12; font-weight: 600; }
.track-status { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; }
.track-status-note { color: var(--faint); font-size: 0.74rem; }
.progress { display: block; width: 120px; height: 6px; overflow: hidden; border-radius: 999px; background: #eef2f6; }
.progress-fill { display: block; height: 100%; border-radius: 999px; background: var(--accent); }
.track-chev { color: var(--faint); }
.track-detail { padding: 2px 18px 18px; border-top: 1px solid var(--line); }
.detail-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
@container act (max-width: 760px) {
  .track-row {
    grid-template-columns: minmax(0, 1fr) auto 16px;
    grid-template-areas: "id id chev" "reading status status";
    gap: 10px 12px;
    padding: 14px 16px;
  }
  .track-id { grid-area: id; }
  .track-chev { grid-area: chev; align-self: start; }
  .track-reading { grid-area: reading; align-items: flex-start; text-align: left; }
  .track-status { grid-area: status; align-items: flex-end; text-align: right; }
  .progress { width: 96px; }
  .track-detail { padding: 2px 16px 16px; }
  .summary { padding: 12px 14px; }
  .learned { padding: 14px 16px; }
}
@container act (max-width: 420px) {
  .act .stage-tab { flex-direction: column; gap: 3px; padding: 6px 2px 8px; }
  .act .stage-label { font-size: 0.8rem; }
  .act .stage-text { align-items: center; }
  .act .sugg-buttons .btn-ghost-sm { padding: 7px 6px; font-size: 0.8rem; white-space: nowrap; }
  .act .sugg-buttons .btn-ghost-sm > .bi:not(.lock-ic) { display: none; }
  .sugg { padding: 14px; }
  .track-row { padding: 12px 14px; }
}

/* Concluded */
.learned { padding: 16px 20px; background: #fff; border: 1px solid var(--line); border-radius: 14px; }
.learned-top { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px 12px; }
.learned-chips { display: flex; gap: 6px; }
.learned-what { margin: 8px 0 4px; color: var(--ink); font-size: 0.95rem; line-height: 1.5; }
.learned-note { margin: 0 0 6px; color: var(--body); font-size: 0.88rem; font-style: italic; }
.learned-measured { margin: 0 0 6px; color: #4F7EB3; font-size: 0.8rem; }

/* Panels (log your own change) */
.panel { margin-bottom: 16px; padding: 16px 18px; border-radius: 14px; }
.panel-head { display: flex; flex-direction: column; gap: 2px; margin-bottom: 10px; }

.lead {
  color: #425461;
  font-size: 0.92rem;
  max-width: 780px;
  margin: 2px 0 16px;
  line-height: 1.5;
}
.empty {
  color: #7a8a96;
  font-style: italic;
  padding: 14px 0;
}

.cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card {
  border: 1px solid #e6edf3;
  border-radius: 12px;
  background: #fff;
  padding: 16px 18px;
  box-shadow: 0 1px 4px rgba(13, 38, 76, 0.04);
}
.card.tracked {
  max-width: 860px;
}
.card.sugg {
  max-width: 780px;
}

.card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.titles {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.zone {
  font-weight: 700;
  color: #2b3a45;
  font-size: 0.98rem;
}
.zone.big {
  font-size: 1.05rem;
}
.since {
  color: #7a8a96;
  font-size: 0.82rem;
}

.lever {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.lever-engagement { background: #eef5ff; color: #4F7EB3; }
.lever-conversion { background: #f3fbf5; color: #2c7a3f; }
.lever-svc { background:#effaf8; color:#0D9488 }
.lever-chk { background:#fdf6e6; color:#a9822c }
.lever-waiting { background: #fff7e6; color: #b66a00; }
.lever-reach { background: #f0f0fb; color: #5b53a6; }

/* Deep-link flash: the card you arrived on from Today's "Act on this →". */
.card.sugg.flash { animation: suggFlash 2.2s ease-out; }
@keyframes suggFlash {
  0%   { box-shadow: 0 0 0 2px #4F7EB3, 0 6px 20px rgba(79,126,179,0.25); }
  70%  { box-shadow: 0 0 0 2px #4F7EB3, 0 6px 20px rgba(79,126,179,0.18); }
  100% { box-shadow: none; }
}

.eur {
  font-weight: 800;
  color: #2c7a3f;
  font-size: 1rem;
  white-space: nowrap;
}
/* Visitors, not money — the reach lever's own colour, so it never reads as a € figure. */
.eur.visitors { color: #5b53a6; }

.action {
  color: #2b3a45;
  font-size: 0.94rem;
  line-height: 1.5;
  margin: 12px 0 6px;
}
.why {
  color: #5a6b78;
  font-size: 0.86rem;
  line-height: 1.5;
  margin: 0 0 12px;
}

.state-row {
  margin: 0 0 12px;
}
.state-chip {
  font-size: 0.74rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #e6edf3;
  color: #7a8a96;
  background: #f7fbff;
}
.chip-considering { color: #4F7EB3; background: #eef5ff; border-color: #d7e6fb; }

.btn-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.btn {
  appearance: none;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.btn-primary { background: #4F7EB3; color: #fff; }
.btn-primary:hover { background: #416a99; }
.btn-soft { background: #f4f7fb; color: #425461; border-color: #e6edf3; }
.btn-soft:hover { background: #eaf0f7; }
.btn-ghost { background: transparent; color: #7a8a96; }
.btn-ghost:hover { color: #425461; }
.btn-danger-ghost { background: transparent; color: #b0464b; border-color: #f0dcdd; }
.btn-danger-ghost:hover { background: #fdf3f3; }

.mini-form {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  background: #f7fbff;
  border: 1px solid #e2ecf7;
  border-radius: 10px;
  padding: 12px 14px;
  margin-top: 4px;
}
.mini-form label {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #425461;
}
.mini-form input,
.mini-form select {
  border: 1px solid #d7e2ee;
  border-radius: 7px;
  padding: 6px 8px;
  font-size: 0.85rem;
  color: #2b3a45;
  background: #fff;
}
.mini-actions {
  display: flex;
  gap: 8px;
}

.verdict-chip {
  font-size: 0.72rem;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
}
.v-early { background: #fff7e6; color: #b66a00; }
.v-improved { background: #f3fbf5; color: #2c7a3f; }
.v-declined { background: #fdf3f3; color: #b0464b; }
.v-flat { background: #f2f5f8; color: #6c7a86; }
.v-mixed { background: #fff7e6; color: #b66a00; }

.read {
  color: #2b3a45;
  font-size: 0.92rem;
  line-height: 1.55;
  margin: 10px 0 10px;
}
/* #3 weather caveat — a quieter, teal-tinted continuation of the read (prose, not a
   boxed chip): present only when weather may have confounded the verdict. */
.read-weather {
  color: #2d6a66;
  font-size: 0.84rem;
  margin-top: -2px;
}
.read-weather .bi { color: #178f8f; margin-right: 3px; }

/* #2 timely weather heads-up strip — deliberately lighter than an opportunity card:
   a teal-tinted band, no € / lever / lifecycle, a single "Noted" dismiss. */
.wx-headsup {
  display: flex; align-items: flex-start; gap: 12px;
  background: linear-gradient(90deg, #ecf8f7, #f5faf2);
  border: 1px solid #d9ece9; border-left: 3px solid #1f9d9d;
  border-radius: 12px; padding: 12px 14px; margin: 0 0 20px;
}
.wx-headsup-ic {
  flex: 0 0 auto; width: 34px; height: 34px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  background: #fff; color: #178f8f; font-size: 1rem;
  box-shadow: 0 1px 3px rgba(20, 60, 55, 0.08);
}
.wx-headsup-body { flex: 1 1 auto; min-width: 0; }
.wx-headsup-title { font-weight: 650; color: #12615f; font-size: 0.92rem; }
.wx-headsup-tag {
  font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px;
  color: #178f8f; background: #dff0ed; border-radius: 999px;
  padding: 2px 7px; margin-right: 8px; vertical-align: middle;
}
.wx-headsup-text { font-size: 0.84rem; color: #45605c; line-height: 1.45; margin-top: 3px; }
.wx-headsup-x {
  flex: 0 0 auto; align-self: center;
  border: 1px solid #cfe4e0; background: #fff; color: #2d6a66;
  font-size: 0.78rem; border-radius: 999px; padding: 4px 12px; cursor: pointer;
}
.wx-headsup-x:hover { background: #eef7f5; }
@media (max-width: 640px) {
  .wx-headsup { flex-wrap: wrap; }
  .wx-headsup-x { align-self: flex-start; }
}

/* € roll-up banner */
.rollup {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 18px;
  background: #f3fbf5;
  border: 1px solid #d9f1df;
  border-radius: 12px;
  padding: 14px 18px;
  margin: 0 0 18px;
  max-width: 860px;
}
.rollup-main { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.rollup-label { color: #2c7a3f; font-weight: 700; font-size: 0.92rem; }
.rollup-eur { color: #2c7a3f; font-weight: 800; font-size: 1.4rem; line-height: 1; }
.rollup-per { font-size: 0.9rem; font-weight: 700; opacity: 0.8; }
.rollup-tag {
  font-size: 0.74rem; font-weight: 700; color: #2c7a3f;
  display: inline-flex; align-items: center; gap: 4px;
  background: #e4f6e9; border-radius: 999px; padding: 2px 9px;
}
.rollup-tag .bi, .rollup-chip .bi { font-size: 0.72rem; }
.rollup-extra { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.rollup-chip {
  font-size: 0.78rem; font-weight: 600;
  border-radius: 999px; padding: 4px 10px;
  display: inline-flex; align-items: center; gap: 4px;
}
.chip-modelled { color: #35618f; background: #eef5ff; border: 1px solid #d7e6fb; }
.chip-muted { color: #7a8a96; background: #fff; border: 1px solid #e6edf3; }

/* Lifetime value delivered — the grand total footer */
.earned { margin-top: 28px; max-width: 860px; }
.earned-inner {
  background: #1a2a36;
  color: #fff;
  border-radius: 14px;
  padding: 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.earned-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: #c3d16a;
  display: flex;
  align-items: center;
  gap: 8px;
}
.earned-main { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.earned-eur { font-size: 2rem; font-weight: 800; color: #fff; line-height: 1; }
.earned-tag { font-size: 0.8rem; opacity: 0.75; }
.earned-extra { display: flex; gap: 8px; }

/* Revenue impact line */
.impact {
  display: flex;
  align-items: center;
  gap: 9px;
  flex-wrap: wrap;
  font-size: 0.92rem;
  padding: 9px 12px;
  border-radius: 9px;
  margin: 4px 0 12px;
  border: 1px solid transparent;
}
.impact .bi-cash-coin { font-size: 0.95rem; }
.impact .imp-text b { font-weight: 800; }
.imp-basis {
  font-size: 0.76rem;
  font-weight: 600;
  opacity: 0.8;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.imp-basis .bi { font-size: 0.72rem; }
.imp-good { background: #f3fbf5; border-color: #d9f1df; color: #2c7a3f; }
.imp-indicative { background: #eef5ff; border-color: #d7e6fb; color: #35618f; }
.imp-early { background: #fff7e6; border-color: #ffe7bf; color: #b66a00; }
.imp-bad { background: #fdf3f3; border-color: #f0dcdd; color: #b0464b; }
.imp-none { background: #f7f9fb; border-color: #eef2f6; color: #7a8a96; }
.imp-na { background: #f7f9fb; border: 1px dashed #dbe3ec; color: #8a97a3; }
.read :deep(strong) { color: #2b3a45; }

.delta-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.delta-chip {
  appearance: none;
  font-family: inherit;
  font-size: 0.78rem;
  color: #5a6b78;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid #e6edf3;
  background: #fbfdff;
  cursor: default;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.delta-chip strong { margin-left: 4px; }
.delta-chip.chip-clickable { cursor: pointer; }
.delta-chip.chip-clickable:hover { border-color: #c7d3df; }
/* the metric currently drawn on the chart */
.delta-chip.chip-active {
  box-shadow: 0 0 0 2px rgba(79, 126, 179, 0.28);
  border-color: #4F7EB3;
}
/* conversion awaiting sales — a prompt, not a value */
.delta-chip.chip-pending {
  color: #4F7EB3;
  border-style: dashed;
  border-color: #b9cde3;
  background: #f5f9fe;
  cursor: pointer;
}
.delta-chip.chip-pending:hover { border-color: #4F7EB3; }
.pending-tag { margin-left: 6px; font-weight: 700; }
.pending-tag .bi { font-size: 0.72rem; }
.delta-chip.up { color: #2c7a3f; border-color: #d9f1df; background: #f5fbf7; }
.delta-chip.up strong { color: #2c7a3f; }
.delta-chip.down { color: #b0464b; border-color: #f0dcdd; background: #fdf6f6; }
.delta-chip.flat { color: #7a8a96; }

/* Greyed "Add action" affordance — looks role-locked (admin-only in prod),
   but stays clickable in the demo so the flow can be tried. */
.btn-add-locked {
  appearance: none;
  border: 1px dashed #cdd6df;
  background: #f5f7fa;
  color: #9aa8b3;
  font-size: 0.82rem;
  font-weight: 600;
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-style: italic;
  transition: color 0.15s ease, border-color 0.15s ease;
}
.btn-add-locked:hover { color: #6c7a86; border-color: #b6c2cd; }

/* per-action sales entry */
.sales-inline {
  border: 1px solid #e2ecf7;
  background: #f9fcff;
  border-radius: 10px;
  padding: 14px 16px;
  margin: 4px 0 14px;
}

.sales-log {
  width: 100%;
  border-collapse: collapse;
  margin-top: 14px;
  font-size: 0.82rem;
}
.sales-log th {
  text-align: left;
  color: #7a8a96;
  font-weight: 700;
  border-bottom: 1px solid #e6edf3;
  padding: 6px 8px;
}
.sales-log td {
  color: #2b3a45;
  border-bottom: 1px solid #f1f5f9;
  padding: 6px 8px;
}

.add-form {
  max-width: 640px;
  border: 1px solid #e2ecf7;
  background: #f9fcff;
  margin-bottom: 18px;
}
.add-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.add-head strong { color: #2b3a45; font-size: 0.98rem; }
.add-note { color: #7a8a96; font-size: 0.78rem; font-style: italic; }

.fld {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #425461;
  margin-bottom: 12px;
}
.fld textarea,
.fld input,
.fld select {
  border: 1px solid #d7e2ee;
  border-radius: 7px;
  padding: 7px 9px;
  font-size: 0.86rem;
  font-weight: 400;
  color: #2b3a45;
  background: #fff;
  font-family: inherit;
}
.fld textarea { resize: vertical; }
.fld-row {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}
.fld-row .fld { flex: 1 1 220px; }

.metric-picks {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  font-weight: 400;
}
.pick {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #2b3a45;
  font-weight: 400;
}
.pick-off { color: #9aa8b3; }
.pick-hint { color: #9aa8b3; font-size: 0.78rem; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }

.chart {
  width: 100%;
  height: 240px;
  margin-top: 6px;
}

/* Conclude panel */
.conclude-panel {
  border: 1px solid #e2ecf7;
  background: #f9fcff;
  border-radius: 10px;
  padding: 14px 16px;
  margin-top: 8px;
}
.attr-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 0;
}
.attr-opt {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 0.88rem;
  color: #2b3a45;
  border: 1px solid #e6edf3;
  border-radius: 8px;
  padding: 9px 12px;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.attr-opt:hover { border-color: #c7d3df; }
.attr-opt.attr-sel { border-color: #4F7EB3; background: #f2f7fd; }

.allen-line {
  color: #4F7EB3;
  font-size: 0.85rem;
  margin: 10px 0 12px;
  display: flex;
  align-items: baseline;
  gap: 7px;
}
.allen-line .bi { font-size: 0.82rem; }

/* Learning ledger */
.card.learned { max-width: 860px; }
.learned-chips { display: flex; gap: 8px; flex-wrap: wrap; }
.attr-chip {
  font-size: 0.72rem;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
}
.attr-good { background: #f3fbf5; color: #2c7a3f; }
.attr-warn { background: #fff7e6; color: #b66a00; }
.attr-bad { background: #fdf3f3; color: #b0464b; }
.attr-flat { background: #f2f5f8; color: #6c7a86; }

/* "tried before" flag on an active suggestion */
.retry-flag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.74rem;
  font-weight: 700;
  color: #b66a00;
  background: #fff7e6;
  border: 1px solid #ffe7bf;
  border-radius: 999px;
  padding: 3px 10px;
  margin: 4px 0 2px;
}
.retry-note {
  color: #8a6a2f;
  font-size: 0.82rem;
  font-style: italic;
  margin: 6px 0 0;
}

/* 6.3: an action in progress on a zone whose type changed today (calm blue) */
.track-type-note { display: block; margin-top: 4px; font-size: 12px; color: #2C5F86; }

/* held-back suggestions (concluded "drop") */
.held-block {
  margin-top: 18px;
  border-top: 1px dashed #e6edf3;
  padding-top: 14px;
  max-width: 780px; /* match the suggestion cards' width */
}
.held-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #9aa8b3;
  margin-bottom: 10px;
}
.held-list { display: flex; flex-direction: column; gap: 8px; }
.held-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  background: #f7f9fb;
  border: 1px solid #eef2f6;
  border-radius: 10px;
  padding: 10px 14px;
  opacity: 0.85;
}
.held-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.held-main .zone { font-size: 0.86rem; }
.held-action {
  color: #6c7a86;
  font-size: 0.86rem;
  text-decoration: line-through;
  text-decoration-color: #c7d3df;
}
.held-reason { color: #9aa8b3; font-size: 0.78rem; font-style: italic; }
.chart-caption {
  color: #7a8a96;
  font-size: 0.78rem;
  margin: 2px 0 12px;
}
</style>
